// frontend/src/pages/SurgeryRequest.js
// Supabase対応版

import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import Header from "../components/ui/Header";
import { supabase } from "../supabase";
import DraggableSurgeryBar from "../DraggableSurgeryBar";
import { useDrop } from "react-dnd";
import DropCell from "../components/ui/DropCell";
import AutoStaffAssignmentModal from "../components/AutoStaffAssignmentModal";
import Papa from "papaparse";
import SurgeryOCRScanner from './SurgeryOCRScanner';
import StaffSelectionModal from '../components/StaffSelectionModal';
import DailyRoleAssignmentPanel from '../components/DailyRoleAssignmentPanel';
import { useOrganization } from "../contexts/OrganizationContext";
import { writeClientAudit } from "../utils/auditClient";
import ExcludedStaffPanel from '../components/ExcludedStaffPanel';
import SurgeryInfoModal from '../components/SurgeryInfoModal';
import { fetchOrgDayShiftMarks } from '../utils/dayShiftPatterns';

// ==========================================
// EnhancedDatePicker（変更なし）
// ==========================================
const EnhancedDatePicker = ({ selectedDate, onDateChange }) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(selectedDate.toDate());

  const today = dayjs().startOf('day');
  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];
  const monthNames = ['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'];

  const getMonthInfo = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    return { firstDay, lastDay, startDate };
  };

  const generateCalendarDays = () => {
    const { startDate } = getMonthInfo(currentMonth);
    const days = [];
    const currentDate = new Date(startDate);
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return days;
  };

  const goToPreviousMonth = () => setCurrentMonth(prev => { const d = new Date(prev); d.setMonth(d.getMonth()-1); return d; });
  const goToNextMonth = () => setCurrentMonth(prev => { const d = new Date(prev); d.setMonth(d.getMonth()+1); return d; });

  const goToPreviousDay = () => {
    const newDate = selectedDate.subtract(1, 'day');
    onDateChange(newDate);
    if (newDate.month() !== currentMonth.getMonth() || newDate.year() !== currentMonth.getFullYear()) {
      setCurrentMonth(newDate.toDate());
    }
  };

  const goToNextDay = () => {
    const newDate = selectedDate.add(1, 'day');
    onDateChange(newDate);
    if (newDate.month() !== currentMonth.getMonth() || newDate.year() !== currentMonth.getFullYear()) {
      setCurrentMonth(newDate.toDate());
    }
  };

  const selectDate = (date) => { onDateChange(dayjs(date)); setIsCalendarOpen(false); };
  const isSameDate = (d1, d2) => dayjs(d1).isSame(dayjs(d2), 'day');
  const isCurrentMonth = (date) => date.getMonth() === currentMonth.getMonth() && date.getFullYear() === currentMonth.getFullYear();
  const formatShortDate = () => `${selectedDate.month()+1}月${selectedDate.date()}日(${weekDays[selectedDate.day()]})`;
  const formatDate = () => selectedDate.format("YYYY年MM月DD日");
  const isToday = selectedDate.isSame(today, 'day');
  const isPast = selectedDate.isBefore(today, 'day');
  const calendarDays = generateCalendarDays();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <button onClick={goToPreviousDay} className="p-3 hover:bg-white rounded-full transition-all duration-200 shadow-sm hover:shadow-md border border-transparent hover:border-blue-200">
          <ChevronLeft className="h-6 w-6 text-gray-600" />
        </button>
        <div className="text-center">
          <div className="text-xl font-bold text-gray-800">{formatShortDate()}</div>
          <div className="text-sm text-gray-500 mt-1">{isToday ? '今日' : isPast ? '過去' : '未来'}</div>
        </div>
        <button onClick={goToNextDay} className="p-3 hover:bg-white rounded-full transition-all duration-200 shadow-sm hover:shadow-md border border-transparent hover:border-blue-200">
          <ChevronRight className="h-6 w-6 text-gray-600" />
        </button>
      </div>

      <div>
        <div className="flex items-center justify-between p-3 border-2 border-blue-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors bg-blue-50 hover:bg-blue-100" onClick={() => setIsCalendarOpen(!isCalendarOpen)}>
          <span className="text-lg font-medium text-gray-800">{formatDate()}</span>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-blue-600">カレンダーで選択</span>
            <Calendar className="h-5 w-5 text-blue-600" />
          </div>
        </div>
      </div>

      {isCalendarOpen && (
        <div className="border border-gray-200 rounded-lg bg-white shadow-lg p-4 z-50 relative">
          <div className="flex items-center justify-between mb-4">
            <button onClick={goToPreviousMonth} className="p-2 hover:bg-gray-100 rounded-full"><ChevronLeft className="h-5 w-5 text-gray-600" /></button>
            <h2 className="text-lg font-bold text-gray-800">{currentMonth.getFullYear()}年{monthNames[currentMonth.getMonth()]}</h2>
            <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-full"><ChevronRight className="h-5 w-5 text-gray-600" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day, index) => (
              <div key={`weekday_${day}_${index}`} className={`text-center text-sm font-medium py-2 ${index===0?'text-red-600':index===6?'text-blue-600':'text-gray-600'}`}>{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((date, index) => {
              const isDateToday = isSameDate(date, today.toDate());
              const isSelected = isSameDate(date, selectedDate.toDate());
              const isThisMonth = isCurrentMonth(date);
              const dayOfWeek = date.getDay();
              return (
                <button key={`calendar_day_${date.getTime()}_${index}`} onClick={() => selectDate(date)}
                  className={`p-2 text-sm rounded-lg transition-all duration-200 hover:bg-blue-100 ${isSelected?'bg-blue-600 text-white hover:bg-blue-700 shadow-md':isDateToday?'bg-blue-100 text-blue-800 font-bold border-2 border-blue-300':isThisMonth?dayOfWeek===0?'text-red-600 hover:bg-red-50':dayOfWeek===6?'text-blue-600 hover:bg-blue-50':'text-gray-800 hover:bg-gray-100':'text-gray-400 hover:bg-gray-50'} ${!isThisMonth?'opacity-50':''}`}>
                  {date.getDate()}
                </button>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-200">
            <button onClick={() => { onDateChange(today); setCurrentMonth(today.toDate()); setIsCalendarOpen(false); }} className="w-full py-2 px-4 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium">
              📅 今日に戻る
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// メインコンポーネント
// ==========================================
function SurgeryRequest() {
  const { currentOrganization } = useOrganization();

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [surgeryData, setSurgeryData] = useState({});
  const [modalInfo, setModalInfo] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });
  const [showRoomSettingsModal, setShowRoomSettingsModal] = useState(false);
  const [roomSettings, setRoomSettings] = useState({
    rooms: [
      { id: "OR1", name: "OR1" },
      { id: "OR2", name: "OR2" },
      { id: "OR3", name: "OR3" },
      { id: "OR4", name: "OR4" }
    ]
  });
  const [showStaffSelection, setShowStaffSelection] = useState(null);
  const [mode, setMode] = useState('registration');
  // 指導通知 - 手動完了ボタンの表示制御（設定で ON/OFF）
  const [manualNotificationEnabled, setManualNotificationEnabled] = useState(true);
  const [completingSurgeryId, setCompletingSurgeryId] = useState(null);
  // ホーム画面への公開反映
  const [publishingHome, setPublishingHome] = useState(false);
  const [selectedSurgeryForInfo, setSelectedSurgeryForInfo] = useState(null);

  const [outpatientSettings, setOutpatientSettings] = useState({
    patterns: {
      weekday: { leader: 1, member: 4, note: "平日パターン" },
      weekend: { leader: 1, member: 2, note: "土日パターン" }
    },
    specialDates: {},
    roles: [
      { id: "leader", name: "リーダー", color: "#3B82F6" },
      { id: "member", name: "メンバー", color: "#10B981" }
    ],
    department: "外来",
    shifts: [
      { id: "day", name: "日勤", start: "08:30", end: "17:00" },
      { id: "evening", name: "準夜勤", start: "16:30", end: "01:00" }
    ]
  });
  const [showOutpatientSettingsModal, setShowOutpatientSettingsModal] = useState(false);
  const [outpatientActiveTab, setOutpatientActiveTab] = useState("patterns");
  const [newPatternName, setNewPatternName] = useState("");
  const [specialDateInput, setSpecialDateInput] = useState({ date: "", leader: 0, member: 0, note: "" });

  const [csvMappingModal, setCsvMappingModal] = useState({ show: false, headers: [], mapping: {}, data: [], fileName: "", preview: [], result: null });
  const [csvImportMenuOpen, setCsvImportMenuOpen] = useState(false);
  // 施設ごとの CSV 取込設定（localStorage 永続化）
  const [csvImportSettings, setCsvImportSettings] = useState({
    tfModeEnabled: false,
    defaultStartTime: '09:00',
  });
  const [availableSkills, setAvailableSkills] = useState([]);
  const [skillsByDepartment, setSkillsByDepartment] = useState({});
  const [departments, setDepartments] = useState([]);
  const [activeSkillDept, setActiveSkillDept] = useState("all");
  const [skillSearchKeyword, setSkillSearchKeyword] = useState("");
  const [showOCRModal, setShowOCRModal] = useState(false);
  const [showAutoAssignModal, setShowAutoAssignModal] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [availableStaffInfo, setAvailableStaffInfo] = useState({ total: 0, available: [], busy: [] });
  const [todayDayShiftStaff, setTodayDayShiftStaff] = useState([]);
  const [procedures, setProcedures] = useState([]);

  const fileInputRef = useRef(null);
  const tableRef = useRef(null);

  // ==========================================
  // ユーティリティ
  // ==========================================
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 3000);
  };

  const getTagColor = (colorName) => {
    const colors = {
      blue: { bg: '#e3f2fd', text: '#1976d2' },
      red: { bg: '#ffebee', text: '#c62828' },
      green: { bg: '#e8f5e9', text: '#2e7d32' },
      orange: { bg: '#fff3e0', text: '#ef6c00' },
      purple: { bg: '#f3e5f5', text: '#7b1fa2' },
      pink: { bg: '#fce4ec', text: '#c2185b' },
      teal: { bg: '#e0f2f1', text: '#00695c' },
      amber: { bg: '#fff8e1', text: '#f57f17' },
    };
    return colors[colorName] || colors.blue;
  };

  // ==========================================
  // データ取得：スタッフ一覧（Supabase）
  // ==========================================
  useEffect(() => {
    const fetchStaffList = async () => {
      try {
        const { data, error } = await supabase
          .from('staff')
          .select('*')
          .eq('organization_id', currentOrganization?.id)
          .eq('is_active', true);

        if (error) throw error;

        const staffData = (data || []).map(s => ({
          ...s,
          displayName: `${s.last_name || ""} ${s.first_name || ""}`.trim(),
          experience: s.year ? new Date().getFullYear() - parseInt(s.year) : 0,
          // Firebase互換フィールド
          employeeId: s.employee_id,
          lastName: s.last_name,
          firstName: s.first_name,
        }));
        setStaffList(staffData);
        console.log("📋 スタッフリスト取得完了:", staffData.length, "名");
      } catch (error) {
        console.error("スタッフリスト取得エラー:", error);
      }
    };
    fetchStaffList();
  }, []);

  // ==========================================
  // データ取得：診療科・術式（Supabase）
  // ==========================================
  useEffect(() => {
    const fetchProceduresAndDepartments = async () => {
      try {
        const [{ data: deptData, error: deptError }, { data: procData, error: procError }] = await Promise.all([
          supabase.from('departments').select('*').eq('organization_id', currentOrganization?.id),
          supabase.from('procedures').select('*').eq('organization_id', currentOrganization?.id)
        ]);

        if (deptError) throw deptError;
        if (procError) throw procError;

        const deptList = deptData || [];
        const procList = (procData || []).map(proc => ({
          ...proc,
          required_roles: typeof proc.required_roles === 'string'
            ? JSON.parse(proc.required_roles)
            : (proc.required_roles || []),
          // Firebase互換
          departmentId: proc.department_id,
        }));

        setDepartments(deptList);
        setProcedures(procList);

        const proceduresByDept = {};
        const allProcedures = [];
        deptList.forEach(dept => {
          const deptProcs = procList.filter(p => p.department_id === dept.id);
          proceduresByDept[dept.name] = deptProcs.map(p => p.name);
          allProcedures.push(...deptProcs.map(p => p.name));
        });

        setSkillsByDepartment(proceduresByDept);
        setAvailableSkills([...new Set(allProcedures)]);
      } catch (error) {
        console.error("術式データの取得エラー:", error);
      }
    };
    fetchProceduresAndDepartments();
  }, []);

  // ==========================================
  // データ取得：手術室設定（Supabase）
  // ==========================================
  useEffect(() => {
    const loadRoomSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('surgery_room_settings')
          .select('*')
          .eq('organization_id', currentOrganization?.id)
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        if (data) setRoomSettings({ rooms: data.rooms });
      } catch (error) {
        console.error("手術室設定の読み込みエラー:", error);
      }
    };
    loadRoomSettings();
  }, []);

  // ==========================================
  // 指導通知設定（手動完了ボタンの表示制御）
  // ==========================================
  useEffect(() => {
    const loadInstructionNotificationSettings = async () => {
      if (!currentOrganization?.id) return;
      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('organization_id', currentOrganization.id)
          .eq('key', 'instruction_notification_manual_enabled')
          .maybeSingle();
        if (error && error.code !== 'PGRST116') throw error;
        setManualNotificationEnabled(data ? data.value !== false : true);
      } catch (error) {
        console.error('指導通知設定の読み込みエラー:', error);
      }
    };
    loadInstructionNotificationSettings();
  }, [currentOrganization?.id]);

  // CSV 取込設定（TFモード・既定開始時刻）を localStorage からロード
  useEffect(() => {
    if (!currentOrganization?.id) return;
    try {
      const raw = localStorage.getItem(`csv_import_settings_${currentOrganization.id}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        setCsvImportSettings(prev => ({
          tfModeEnabled: !!parsed.tfModeEnabled,
          defaultStartTime: parsed.defaultStartTime || prev.defaultStartTime || '09:00',
        }));
      }
    } catch (e) {
      console.warn('CSV取込設定の読み込みに失敗:', e?.message);
    }
  }, [currentOrganization?.id]);

  const persistCsvImportSettings = (next) => {
    setCsvImportSettings(next);
    if (currentOrganization?.id) {
      try {
        localStorage.setItem(`csv_import_settings_${currentOrganization.id}`, JSON.stringify(next));
      } catch (e) { /* ignore quota */ }
    }
  };

  // その日の手術配置を全員のホーム画面に公開
  const handlePublishToHome = async () => {
    if (!currentOrganization?.id) return;
    const dateStr = selectedDate.format('YYYY-MM-DD');
    const targets = Object.values(surgeryData).filter(s => s.docId);
    if (targets.length === 0) {
      showNotification('公開対象の手術がありません', 'error');
      return;
    }

    const unpublished = targets.filter(s => !s.published_at);
    const alreadyPublished = targets.length - unpublished.length;
    const message = unpublished.length === 0
      ? `${dateStr} の手術はすべて公開済みです。再度公開しますか？\n（タイムスタンプを上書きします）`
      : `${dateStr} の手術 ${targets.length} 件をホーム画面に公開します。\n` +
        (alreadyPublished > 0 ? `（うち ${alreadyPublished} 件は公開済み、新規 ${unpublished.length} 件）\n` : '') +
        '\n配置されたスタッフのホーム画面に表示されるようになります。よろしいですか？';
    if (!window.confirm(message)) return;

    setPublishingHome(true);
    try {
      const now = new Date().toISOString();
      const surgeryIds = targets.map(s => s.docId);
      const { error } = await supabase
        .from('surgery_schedules')
        .update({ published_at: now, updated_at: now })
        .in('id', surgeryIds)
        .eq('organization_id', currentOrganization.id);
      if (error) throw error;

      // ローカル state も即時反映
      setSurgeryData(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(key => {
          if (surgeryIds.includes(next[key].docId)) {
            next[key] = { ...next[key], published_at: now };
          }
        });
        return next;
      });

      // 「配置が確定しました」通知を配置者へ作成（重複は staff_id 単位で1通にまとめる）
      const toArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);
      const recipientIds = new Set();
      targets.forEach((s) => {
        [...toArr(s.scrub), ...toArr(s.circulating), ...toArr(s.trainer), ...toArr(s.trainee)]
          .forEach((p) => {
            if (p?.id) recipientIds.add(String(p.id));
          });
      });

      let notifCount = 0;
      if (recipientIds.size > 0) {
        const dateLabel = dateStr;
        const rows = Array.from(recipientIds).map((rid) => ({
          organization_id: currentOrganization.id,
          recipient_staff_id: rid,
          type: 'assignment_confirmed',
          title: `${dateLabel} の手術配置が確定しました`,
          body: `${dateLabel} の手術配置がホーム画面に反映されました。ご確認ください。`,
          surgery_date: dateStr,
        }));
        const { error: notifError } = await supabase
          .from('staff_notifications')
          .insert(rows);
        if (notifError) {
          console.warn('⚠️ 配置確定通知の作成に失敗:', notifError);
        } else {
          notifCount = rows.length;
        }
      }

      showNotification(
        `ホーム画面に反映しました（${targets.length} 件）` +
          (notifCount > 0 ? ` / ${notifCount} 名に通知` : ''),
        'success'
      );
    } catch (error) {
      console.error('ホーム反映エラー:', error);
      showNotification(error.message || '反映に失敗しました', 'error');
    } finally {
      setPublishingHome(false);
    }
  };

  // 手術完了ハンドラ
  const handleCompleteSurgery = async (surgery) => {
    if (!surgery?.docId || !currentOrganization?.id) return;
    const ok = window.confirm(
      `手術「${surgery.procedure || '予定手術'}」を完了状態にしますか？\n\n` +
      '指導者が配置されていれば、該当者に経験録更新の通知が送られます。'
    );
    if (!ok) return;

    setCompletingSurgeryId(surgery.docId);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('セッションが見つかりません。再ログインしてください。');

      const SUPABASE_URL = 'https://tpxzotpgkcaioifgzlii.supabase.co';
      const res = await fetch(`${SUPABASE_URL}/functions/v1/complete-surgery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          surgeryId: surgery.docId,
          organizationId: currentOrganization.id,
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || '完了処理に失敗しました');

      const created = json.notifications?.created ?? 0;
      const msg = json.alreadyCompleted
        ? 'この手術は既に完了済みです'
        : created > 0
          ? `完了しました（${created} 件の指導通知を送信）`
          : '完了しました（指導通知の対象なし）';
      showNotification(msg, 'success');

      // 画面上の手術データを更新
      setSurgeryData(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(key => {
          if (next[key].docId === surgery.docId) {
            next[key] = { ...next[key], completed_at: json.completed_at };
          }
        });
        return next;
      });
      setModalInfo(null);
    } catch (error) {
      console.error('手術完了処理エラー:', error);
      showNotification(error.message || '完了処理に失敗しました', 'error');
    } finally {
      setCompletingSurgeryId(null);
    }
  };

  // ==========================================
  // データ取得：外来設定（Supabase）
  // ==========================================
  useEffect(() => {
    const loadOutpatientSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('outpatient_settings')
          .select('*')
          .eq('organization_id', currentOrganization?.id)
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        if (data) {
          setOutpatientSettings({
            patterns: data.patterns || outpatientSettings.patterns,
            specialDates: data.special_dates || {},
            roles: data.roles || outpatientSettings.roles,
          });
        }
      } catch (error) {
        console.error("外来設定の読み込みエラー:", error);
      }
    };
    loadOutpatientSettings();
  }, []);

  // ==========================================
  // データ取得：手術予定（Supabase）
  // ==========================================
  const fetchSurgeryData = async () => {
    setLoadingData(true);
    try {
      const { data, error } = await supabase
        .from('surgery_schedules')
        .select('*')
        .eq('organization_id', currentOrganization?.id)
        .eq('date', selectedDate.format("YYYY-MM-DD"));

      if (error) throw error;

      const loadedData = {};
      (data || []).forEach((d, docIndex) => {
        const start = dayjs(`${d.date} ${d.start_time}`);
        const end = dayjs(`${d.date} ${d.end_time}`);
        const durationMinutes = end.diff(start, "minute");
        const key = `${d.date}_${d.room}_${d.start_time}_${d.id}_${docIndex}`;
        loadedData[key] = {
          ...d,
          docId: d.id,
          // Firebase互換フィールド
          start: d.start_time,
          end: d.end_time,
          requiredSkills: d.required_skills || [],
          skillCondition: d.skill_condition || 'AND',
          durationMinutes,
          uniqueKey: key,
          scrub: d.scrub ? { ...d.scrub, name: d.scrub.name || '' } : null,
          circulating: d.circulating ? { ...d.circulating, name: d.circulating.name || '' } : null,
        };
      });

      setSurgeryData(loadedData);
      console.log("手術データ読み込み完了:", Object.keys(loadedData).length, "件");

      setTimeout(() => calculateAvailableStaff(), 500);
    } catch (error) {
      console.error("スケジュール取得エラー:", error);
      showNotification("スケジュールの取得に失敗しました", "error");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchSurgeryData();
  }, [selectedDate]);

  // ==========================================
  // データ取得：日勤者（Supabase monthly_shifts）
  // ==========================================
  const fetchTodayDayShiftStaff = async () => {
    try {
      if (!staffList.length) return [];

      const today = selectedDate.format("YYYY-MM-DD");
      console.log("🔍 日勤者検索開始:", today, "/ 全スタッフ数:", staffList.length);

      // 「人員配置モードで使用する」とマークされた日勤パターンの short_name 群を取得
      const dayShiftMarks = await fetchOrgDayShiftMarks(currentOrganization?.id);
      console.log("🎯 人員配置モード対象マーク:", Array.from(dayShiftMarks));

      // monthly_shifts から当日データを取得
      const { data, error } = await supabase
        .from('monthly_shifts')
        .select('employee_id, shift_type')
        .eq('organization_id', currentOrganization?.id)
        .eq('shift_date', today);

      if (error) throw error;

      // shift_type が日勤マークに該当する employee_id を日勤者とする（インクルーシブ判定）
      const dayShiftEmployeeIds = new Set(
        (data || [])
          .filter((row) => row.shift_type && dayShiftMarks.has(String(row.shift_type)))
          .map((row) => String(row.employee_id))
      );

      const dayShiftStaff = staffList.filter((staff) =>
        dayShiftEmployeeIds.has(String(staff.employee_id))
      );

      console.log("✅ 日勤者:", dayShiftStaff.length, "名");
      setTodayDayShiftStaff(dayShiftStaff);
      return dayShiftStaff;
    } catch (error) {
      console.error("❌ 日勤者取得エラー:", error);
      setTodayDayShiftStaff([]);
      return [];
    }
  };

  useEffect(() => {
    if (staffList.length > 0) {
      fetchTodayDayShiftStaff().then(() => {
        setTimeout(() => calculateAvailableStaff(), 100);
      });
    }
  }, [selectedDate, staffList]);

  useEffect(() => {
    if (todayDayShiftStaff.length > 0) calculateAvailableStaff();
  }, [todayDayShiftStaff, surgeryData]);

  // ==========================================
  // 空き人員計算
  // ==========================================
  const calculateAvailableStaff = () => {
    const dayShiftStaff = todayDayShiftStaff;
    if (!dayShiftStaff.length) {
      setAvailableStaffInfo({ total: 0, available: [], busy: [] });
      return;
    }

    const now = dayjs();
    const busy = new Set();
    Object.values(surgeryData).forEach(surgery => {
      if (surgery.date === selectedDate.format("YYYY-MM-DD")) {
        const surgeryStart = dayjs(`${surgery.date} ${surgery.start}`);
        const surgeryEnd = dayjs(`${surgery.date} ${surgery.end}`);
        if (now.isAfter(surgeryStart) && now.isBefore(surgeryEnd)) {
          if (surgery.scrub?.employeeId) busy.add(surgery.scrub.employeeId);
          if (surgery.circulating?.employeeId) busy.add(surgery.circulating.employeeId);
          if (surgery.trainer?.employeeId) busy.add(surgery.trainer.employeeId);
          if (surgery.trainee?.employeeId) busy.add(surgery.trainee.employeeId);
        }
      }
    });

    const available = dayShiftStaff.filter(s => !busy.has(s.employee_id));
    const busyStaff = dayShiftStaff.filter(s => busy.has(s.employee_id));
    setAvailableStaffInfo({ total: dayShiftStaff.length, available, busy: busyStaff });
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
      calculateAvailableStaff();
    }, 10000);
    return () => clearInterval(timer);
  }, [surgeryData, selectedDate, todayDayShiftStaff]);

  // ==========================================
  // 自動スクロール
  // ==========================================
  useEffect(() => {
    if (!loadingData && tableRef.current) {
      const now = new Date();
      const sel = selectedDate.toDate();
      const isToday = sel.getFullYear()===now.getFullYear() && sel.getMonth()===now.getMonth() && sel.getDate()===now.getDate();
      if (isToday) {
        setTimeout(() => {
          const scrollContainer = tableRef.current?.parentElement;
          if (scrollContainer) {
            const targetHour = Math.max(0, now.getHours() - 1);
            scrollContainer.scrollLeft = targetHour * 4 * 48;
          }
        }, 100);
      }
    }
  }, [loadingData, selectedDate]);

  // ==========================================
  // 手術予定の保存（Supabase）
  // ==========================================
  const saveSurgery = async (data, isDelete = false) => {
    try {
      if (isDelete) {
        const { error } = await supabase
          .from('surgery_schedules')
          .delete()
          .eq('id', data.docId);
        if (error) throw error;
        showNotification("手術予定を削除しました", "success");
      } else {
        const payload = {
          organization_id: currentOrganization?.id,
          date: selectedDate.format("YYYY-MM-DD"),
          room: data.room,
          procedure: data.procedure || "",
          department: data.department || "",
          surgeon: data.surgeon || "",
          start_time: data.start,
          end_time: data.end,
          priority: data.priority || "normal",
          position: data.position || "",
          anesthesia: data.anesthesia || "",
          required_skills: data.requiredSkills || [],
          skill_condition: data.skillCondition || "AND",
          updated_at: new Date().toISOString(),
        };

        if (data.docId) {
          const { error } = await supabase
            .from('surgery_schedules')
            .update(payload)
            .eq('id', data.docId);
          if (error) throw error;
          showNotification("手術予定を更新しました", "success");
        } else {
          const { error } = await supabase
            .from('surgery_schedules')
            .insert(payload);
          if (error) throw error;
          showNotification("手術予定を登録しました", "success");
        }
      }

      await fetchSurgeryData();
      setModalInfo(null);
      setActiveSkillDept("all");
      setSkillSearchKeyword("");
    } catch (error) {
      console.error("保存エラー:", error);
      showNotification("保存に失敗しました", "error");
    }
  };

  // ==========================================
  // スタッフ配置の保存（Supabase）
  // ==========================================
  // staff_assignmentsへ1件保存するヘルパー
  //
  // context: {
  //   pairingMode: 'instructor'|'trainee'|'solo',
  //   pairedWith: { id, employee_id, name } | null,
  //   levelAtAssignment: string | null,
  //   procedureId: string | null,
  // }
  const saveToStaffAssignments = async (staffInfo, role, surgery, context = {}) => {
    if (!staffInfo) return;
    try {
      const assignmentDate = surgery.date || selectedDate.format('YYYY-MM-DD');
      const procedureName = surgery.procedure || surgery.procedure_name || null;
      const departmentName = surgery.department || surgery.department_name || null;

      const payload = {
        procedure_name: procedureName,
        department_name: departmentName,
        procedure_id: context.procedureId || null,
        pairing_mode: context.pairingMode || null,
        paired_with_staff_id: context.pairedWith?.id || null,
        paired_with_employee_id: context.pairedWith?.employee_id || null,
        paired_with_name: context.pairedWith?.name || null,
        level_at_assignment: context.levelAtAssignment || null,
      };

      // 既存レコード確認（同日・同スタッフ・同役割・同手術）
      const { data: existing } = await supabase
        .from('staff_assignments')
        .select('id')
        .eq('organization_id', currentOrganization?.id)
        .eq('staff_id', staffInfo.id)
        .eq('assignment_date', assignmentDate)
        .eq('role', role)
        .eq('surgery_id', surgery.docId);

      if (existing && existing.length > 0) {
        // 既存レコード更新（経験録更新済の level_after_assignment は上書きしない）
        await supabase
          .from('staff_assignments')
          .update(payload)
          .eq('id', existing[0].id);
      } else {
        // 新規挿入
        await supabase.from('staff_assignments').insert({
          ...payload,
          organization_id: currentOrganization?.id,
          staff_id: staffInfo.id,
          employee_id: staffInfo.employee_id || staffInfo.employeeId,
          assignment_date: assignmentDate,
          role,
          surgery_id: surgery.docId,
        });
      }
    } catch (e) {
      console.warn('staff_assignments保存エラー（継続）:', e.message);
    }
  };

  // instructionRole から pairing_mode への変換
  //   'instructor' -> 'instructor'
  //   'trainee'    -> 'trainee'
  //   'normal'     -> 'solo' (ペアが居なければ独立扱い)
  const mapInstructionRoleToPairingMode = (role, hasPair) => {
    if (role === 'instructor') return 'instructor';
    if (role === 'trainee') return 'trainee';
    if (hasPair) return null; // 役割不明だがペアは存在 -> 推測不可
    return 'solo';
  };

  // その日の全配置済み手術をstaff_assignmentsに一括保存（配列対応）
  const saveAllAssignmentsToHistory = async () => {
    const surgeries = Object.values(surgeryData);
    const targets = surgeries.filter(s => s.scrub || s.circulating);
    if (targets.length === 0) {
      showNotification('保存対象の配置データがありません', 'warning');
      return;
    }

    const empToStaff = {};
    staffList.forEach(s => { empToStaff[String(s.employee_id)] = s; });

    // procedure name -> id 解決マップ（skill_records 連携用）
    const procNameToId = {};
    procedures.forEach(p => { if (p.name) procNameToId[p.name] = p.id; });

    const toArr = (v) => !v ? [] : Array.isArray(v) ? v : [v];

    let count = 0;
    for (const surgery of targets) {
      const procedureId = procNameToId[surgery.procedure || surgery.procedure_name] || null;

      // 同じ役割 (scrub/circulating) ごとに、ペア相手を計算して保存
      for (const roleKey of ['scrub', 'circulating']) {
        const items = toArr(surgery[roleKey]);
        if (items.length === 0) continue;

        items.forEach((it, idx) => { it._idx = idx; });

        for (const item of items) {
          if (!item?.employeeId) continue;
          const s = empToStaff[String(item.employeeId)];
          if (!s) continue;

          // ペア相手 = 同じ surgery + 同じ役割の他スタッフのうち、最初の一人
          // （複数人いる場合は、instructor/trainee の組合せを優先）
          const others = items.filter(o => o.employeeId && o.employeeId !== item.employeeId);
          let pair = null;
          if (others.length > 0) {
            if (item.instructionRole === 'instructor') {
              pair = others.find(o => o.instructionRole === 'trainee') || others[0];
            } else if (item.instructionRole === 'trainee') {
              pair = others.find(o => o.instructionRole === 'instructor') || others[0];
            } else {
              pair = others[0];
            }
          }

          const pairedWith = pair ? {
            id: empToStaff[String(pair.employeeId)]?.id || null,
            employee_id: pair.employeeId,
            name: pair.name || null,
          } : null;

          const pairingMode = mapInstructionRoleToPairingMode(item.instructionRole, !!pair);

          await saveToStaffAssignments(s, roleKey, surgery, {
            pairingMode,
            pairedWith,
            levelAtAssignment: item.level || s.skillLevel || null,
            procedureId,
          });
          count++;
        }
      }
    }
    showNotification(`${count}件の配置履歴を保存しました`, 'success');
  };

  const handleStaffSelect = async (assignmentData) => {
    try {
      const surgery = showStaffSelection.surgery;

      if (!surgery.docId) {
        showNotification("手術データが不正です", "error");
        return;
      }

      // 🔥 同じ手術内で同じスタッフが複数の role に重複登録されるのを防ぐ。
      //   ・scrub と circulating の両方に同じ staff.id → 重複
      //   ・同 role の allStaff 内に同じ staff.id が複数 → 重複
      //   保存前に検出し、エラー通知して中断する。
      const collectIds = (slot) => {
        if (!slot) return [];
        const { staff, allStaff } = slot;
        if (Array.isArray(allStaff) && allStaff.length > 0) {
          return allStaff.map((s) => s?.id).filter(Boolean);
        }
        if (staff?.id) return [staff.id];
        return [];
      };
      const nameOf = (slot, id) => {
        if (!slot) return id;
        const { staff, allStaff } = slot;
        const list = Array.isArray(allStaff) && allStaff.length > 0 ? allStaff : (staff ? [staff] : []);
        const hit = list.find((s) => s?.id === id);
        if (!hit) return id;
        return hit.name || `${hit.last_name || ''} ${hit.first_name || ''}`.trim() || id;
      };
      const scrubIds = collectIds(assignmentData.scrub);
      const circulatingIds = collectIds(assignmentData.circulating);

      // (1) 同 role 内の重複
      const findDupWithin = (ids) => {
        const seen = new Set();
        const dup = new Set();
        ids.forEach((id) => { if (seen.has(id)) dup.add(id); seen.add(id); });
        return [...dup];
      };
      const scrubDupWithin = findDupWithin(scrubIds);
      const circDupWithin = findDupWithin(circulatingIds);
      if (scrubDupWithin.length > 0) {
        const names = scrubDupWithin.map((id) => nameOf(assignmentData.scrub, id));
        showNotification(`器械出しに同じスタッフが重複しています: ${names.join(', ')}`, 'error');
        return;
      }
      if (circDupWithin.length > 0) {
        const names = circDupWithin.map((id) => nameOf(assignmentData.circulating, id));
        showNotification(`外回りに同じスタッフが重複しています: ${names.join(', ')}`, 'error');
        return;
      }

      // (2) scrub × circulating 間の重複
      const crossDup = scrubIds.filter((id) => circulatingIds.includes(id));
      if (crossDup.length > 0) {
        const names = crossDup.map((id) =>
          nameOf(assignmentData.scrub, id) || nameOf(assignmentData.circulating, id));
        showNotification(
          `同じスタッフを器械出しと外回りの両方に配置できません: ${names.join(', ')}`,
          'error'
        );
        return;
      }

      // 両方null = 配置を完全に解除
      if (assignmentData.scrub === null && assignmentData.circulating === null) {
        const { error } = await supabase
          .from('surgery_schedules')
          .update({ scrub: null, circulating: null, updated_at: new Date().toISOString() })
          .eq('id', surgery.docId);
        if (error) throw error;
        showNotification('配置を解除しました', 'success');
        setSurgeryData(prevData => {
          const updatedData = { ...prevData };
          Object.keys(updatedData).forEach(key => {
            if (updatedData[key].docId === surgery.docId) {
              updatedData[key] = { ...updatedData[key], scrub: null, circulating: null };
            }
          });
          return updatedData;
        });
        setShowStaffSelection(null);
        setTimeout(() => calculateAvailableStaff(), 100);
        return;
      }

      const updates = { updated_at: new Date().toISOString() };

      // 複数人対応: allStaffが存在する場合は配列で保存、なければ単一オブジェクト（後方互換）
      // 指導関係の役割（instructionRole: normal/instructor/trainee）も保存する
      if (assignmentData.scrub) {
        const { staff, type, level, count, allStaff } = assignmentData.scrub;
        if (allStaff && allStaff.length > 1) {
          // 複数人: 配列で保存
          updates.scrub = allStaff.map(s => ({
            id: s.id,
            name: s.name || `${s.last_name || ''} ${s.first_name || ''}`.trim(),
            employeeId: s.employee_id,
            level: s.skillLevel || level,
            instructionRole: s.instructionRole || 'normal',
          }));
        } else {
          updates.scrub = {
            id: staff.id,
            name: staff.name || `${staff.last_name || ''} ${staff.first_name || ''}`.trim(),
            employeeId: staff.employee_id,
            level,
            count,
            note: type === 'jiritsu' ? '自立者として配置' : '育成対象として配置',
            instructionRole: staff.instructionRole || 'normal',
          };
        }
      }
      if (assignmentData.circulating) {
        const { staff, type, level, count, allStaff } = assignmentData.circulating;
        if (allStaff && allStaff.length > 1) {
          updates.circulating = allStaff.map(s => ({
            id: s.id,
            name: s.name || `${s.last_name || ''} ${s.first_name || ''}`.trim(),
            employeeId: s.employee_id,
            level: s.skillLevel || level,
            instructionRole: s.instructionRole || 'normal',
          }));
        } else {
          updates.circulating = {
            id: staff.id,
            name: staff.name || `${staff.last_name || ''} ${staff.first_name || ''}`.trim(),
            employeeId: staff.employee_id,
            level,
            count,
            note: type === 'jiritsu' ? '自立者として配置' : '育成対象として配置',
            instructionRole: staff.instructionRole || 'normal',
          };
        }
      }

      const { error } = await supabase
        .from('surgery_schedules')
        .update(updates)
        .eq('id', surgery.docId);

      if (error) throw error;

      const roles = [];
      if (assignmentData.scrub) roles.push('器械出し');
      if (assignmentData.circulating) roles.push('外回り');
      showNotification(`人員配置を完了しました (${roles.join('、')})`, 'success');

      setSurgeryData(prevData => {
        const updatedData = { ...prevData };
        Object.keys(updatedData).forEach(key => {
          if (updatedData[key].docId === surgery.docId) {
            updatedData[key] = {
              ...updatedData[key],
              scrub: updates.scrub !== undefined ? updates.scrub : updatedData[key].scrub,
              circulating: updates.circulating !== undefined ? updates.circulating : updatedData[key].circulating,
            };
          }
        });
        return updatedData;
      });

      setShowStaffSelection(null);
      setTimeout(() => calculateAvailableStaff(), 100);
    } catch (error) {
      console.error('❌ 配置エラー:', error);
      showNotification(`配置に失敗しました: ${error.message}`, 'error');
    }
  };

  // 自動配置の適用
  const handleApplyAssignments = async (assignments, outpatientAssignments, educationStats) => {
    try {
      const promises = Object.keys(assignments).map(async (surgeryId) => {
        const assignment = assignments[surgeryId];
        const updateData = { updated_at: new Date().toISOString() };

        if (assignment.scrub) {
          updateData.scrub = {
            name: assignment.scrub.name || `${assignment.scrub.lastName} ${assignment.scrub.firstName}`,
            employeeId: assignment.scrub.employeeId,
            experience: assignment.scrub.experienceYears || assignment.scrub.experience || 0
          };
        }
        if (assignment.circulating) {
          updateData.circulating = {
            name: assignment.circulating.name || `${assignment.circulating.lastName} ${assignment.circulating.firstName}`,
            employeeId: assignment.circulating.employeeId,
            experience: assignment.circulating.experienceYears || assignment.circulating.experience || 0
          };
        }

        const { error } = await supabase
          .from('surgery_schedules')
          .update(updateData)
          .eq('id', surgeryId);
        if (error) throw error;
      });

      await Promise.all(promises);

      // 外来配置の保存
      if (outpatientAssignments && Object.keys(outpatientAssignments).length > 0) {
        await supabase
          .from('outpatient_assignments')
          .upsert({
            organization_id: currentOrganization?.id,
            date: selectedDate.format("YYYY-MM-DD"),
            assignments: outpatientAssignments,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'organization_id,date' });
      }

      await fetchSurgeryData();
      setShowAutoAssignModal(false);

      const totalAssigned = Object.keys(assignments).length;
      const educationCount = educationStats?.totalEducationOpportunities || 0;
      alert(`配置を適用しました!\n• 手術配置: ${totalAssigned}件\n• 教育配置: ${educationCount}件\n• 外来配置: ${outpatientAssignments ? Object.keys(outpatientAssignments).length : 0}件`);
    } catch (error) {
      console.error("❌ 配置適用エラー:", error);
      alert(`配置の適用に失敗しました: ${error.message}`);
    }
  };

  // ==========================================
  // 手術室設定の保存（Supabase）
  // ==========================================
  const saveRoomSettings = async () => {
    try {
      const { data: existing } = await supabase
        .from('surgery_room_settings')
        .select('id')
        .eq('organization_id', currentOrganization?.id)
        .single();

      if (existing) {
        await supabase.from('surgery_room_settings')
          .update({ rooms: roomSettings.rooms, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
      } else {
        await supabase.from('surgery_room_settings')
          .insert({ organization_id: currentOrganization?.id, rooms: roomSettings.rooms });
      }

      showNotification("手術室設定を保存しました", "success");
      setShowRoomSettingsModal(false);
    } catch (error) {
      console.error("手術室設定保存エラー:", error);
      showNotification("手術室設定の保存に失敗しました", "error");
    }
  };

  // ==========================================
  // 外来設定の保存（Supabase）
  // ==========================================
  const saveOutpatientSettings = async () => {
    try {
      const { data: existing } = await supabase
        .from('outpatient_settings')
        .select('id')
        .eq('organization_id', currentOrganization?.id)
        .single();

      const payload = {
        organization_id: currentOrganization?.id,
        patterns: outpatientSettings.patterns,
        special_dates: outpatientSettings.specialDates,
        roles: outpatientSettings.roles,
        updated_at: new Date().toISOString(),
      };

      if (existing) {
        await supabase.from('outpatient_settings').update(payload).eq('id', existing.id);
      } else {
        await supabase.from('outpatient_settings').insert(payload);
      }

      showNotification("外来設定を保存しました", "success");
      setShowOutpatientSettingsModal(false);
    } catch (error) {
      console.error("外来設定保存エラー:", error);
      showNotification("外来設定の保存に失敗しました", "error");
    }
  };

  // ==========================================
  // データ更新
  // ==========================================
  const refreshData = async () => {
    await fetchSurgeryData();
    showNotification("スケジュールを更新しました", "success");
  };

  // ==========================================
  // CSVインポート（Supabase）
  // ==========================================
  // TFモード時:
  //   - 開始時刻欄が "TF" / "TF1" / "TF2" などの場合、その手術は「タイムフリー」扱い。
  //   - 同じ部屋の直前の手術の終了時刻に続けて配置（前が無ければ defaultStartTime）。
  //   - 終了時刻は「占有時間」（duration カラム）から算出。
  //   - 通常行（時刻が入っているもの）はそのまま使う。
  // ==========================================
  const isTfMarker = (val) => {
    if (!val) return false;
    return /^T\.?F\.?\d*$/i.test(String(val).trim());
  };
  // TF ラベルから番号を取り出す: "TF1"→1, "TF2"→2, "TF"→0。TFでなければ null。
  //   ソート用: CSV の並び順に依らず TF1 → TF2 → TF3 の順に配置するため。
  const getTfNumber = (val) => {
    if (!val) return null;
    const m = String(val).trim().match(/^T\.?F\.?(\d*)$/i);
    if (!m) return null;
    return m[1] ? Number(m[1]) : 0;
  };

  // 占有時間文字列を分に変換（"1:30" "90" "1h30m" "1時間30分" 全部対応）
  const parseDurationMinutes = (raw) => {
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!s) return null;
    if (/^\d{1,2}:\d{2}$/.test(s)) {
      const [h, m] = s.split(':').map(Number);
      return h * 60 + m;
    }
    let total = 0;
    let matched = false;
    const hM = s.match(/(\d+(?:\.\d+)?)\s*(?:時間|hours?|hrs?|h)/i);
    if (hM) { total += parseFloat(hM[1]) * 60; matched = true; }
    const mM = s.match(/(\d+(?:\.\d+)?)\s*(?:分|mins?|minutes?|m)\b/i);
    if (mM) { total += parseFloat(mM[1]); matched = true; }
    if (matched) return Math.round(total);
    // 数値のみ -> 分として解釈
    const n = parseFloat(s);
    if (Number.isFinite(n) && /^\d+(?:\.\d+)?$/.test(s)) return Math.round(n);
    return null;
  };

  const addMinutesToHHMM = (hhmm, minutes) => {
    const [h, m] = hhmm.split(':').map(Number);
    const total = h * 60 + m + minutes;
    const nh = Math.floor(total / 60) % 24;
    const nm = total % 60;
    return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
  };

  const importCsvData = async () => {
    const { mapping, data } = csvMappingModal;
    const tfMode = !!csvImportSettings.tfModeEnabled;
    const defaultStart = csvImportSettings.defaultStartTime || '09:00';

    if (!mapping.room || !mapping.start) {
      showNotification('必須フィールド（部屋、開始時間）のマッピングが必要です', 'warning');
      return;
    }
    if (!tfMode && !mapping.end) {
      showNotification('通常モードでは終了時間のマッピングが必須です', 'warning');
      return;
    }
    if (tfMode && !mapping.duration) {
      showNotification('TFモードでは占有時間のマッピングが必須です', 'warning');
      return;
    }
    if (!currentOrganization?.id) {
      showNotification('組織情報が読み込まれていません。再読み込みしてください', 'error');
      return;
    }

    // 術式名 → procedure {id, name} の正規化用マップ（大小・前後空白を無視）
    const procByName = new Map();
    procedures.forEach((p) => {
      if (p?.name) procByName.set(String(p.name).trim().toLowerCase(), p);
    });

    const buildRequiredSkills = (raw) => {
      if (!raw) return { skills: [], unmatched: [] };
      const names = String(raw)
        .split(/[,、，]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const skills = [];
      const unmatched = [];
      names.forEach((name) => {
        const hit = procByName.get(name.toLowerCase());
        if (hit) {
          skills.push({ id: hit.id, name: hit.name });
        } else {
          // マスタに無くても登録は続行（名前のみ保持）。配置モードのスキル照合は効かないことを通知。
          skills.push({ id: null, name });
          unmatched.push(name);
        }
      });
      return { skills, unmatched };
    };

    try {
      setLoadingData(true);
      const rowsToInsert = [];
      const skipReasons = [];
      const unmatchedSkillSet = new Set();
      const tfPlaced = [];   // TF行の結果サマリ

      // 部屋ごとに「現在の終了時刻ポインタ」を保持しつつ処理
      const lastEndByRoom = {};

      // 🔥 CSV 行を room ごとにソート（同 room 内: 固定時刻 → TF番号順）。
      //   こうしないと CSV のレコード順がそのまま配置順になり、
      //   「9:00 開始指定が TF2 のあとに来ている」CSV だと TF2 が defaultStart
      //   から始まってしまう。固定時刻を先に処理して lastEndByRoom を確定し、
      //   TF はその後に番号順で詰める。
      const indexedData = data.map((row, idx) => ({ row, idx }));
      const byRoom = {};
      indexedData.forEach((item) => {
        const r = String(item.row[mapping.room] || '').trim();
        if (!byRoom[r]) byRoom[r] = [];
        byRoom[r].push(item);
      });
      Object.keys(byRoom).forEach((room) => {
        byRoom[room].sort((a, b) => {
          const aRaw = a.row[mapping.start];
          const bRaw = b.row[mapping.start];
          const aTf = tfMode && isTfMarker(aRaw);
          const bTf = tfMode && isTfMarker(bRaw);
          if (aTf && !bTf) return 1;   // 固定時刻が先
          if (!aTf && bTf) return -1;
          if (aTf && bTf) {
            // TF 同士 → 番号順 (TF1, TF2, TF3...)、番号なし TF は最後
            const an = getTfNumber(aRaw);
            const bn = getTfNumber(bRaw);
            return (an ?? 999) - (bn ?? 999);
          }
          // 固定時刻同士 → 時刻順
          const at = formatTimeString(aRaw) || '99:99';
          const bt = formatTimeString(bRaw) || '99:99';
          return at.localeCompare(bt);
        });
      });
      // 配列に戻す（room ごとのブロック順は出現順を維持）
      const seenRooms = new Set();
      const sortedItems = [];
      indexedData.forEach((item) => {
        const r = String(item.row[mapping.room] || '').trim();
        if (seenRooms.has(r)) return;
        seenRooms.add(r);
        sortedItems.push(...byRoom[r]);
      });

      sortedItems.forEach(({ row, idx }) => {
        const roomVal = row[mapping.room];
        const startRaw = row[mapping.start];
        if (!roomVal || !String(roomVal).trim()) {
          skipReasons.push(`行${idx + 2}: 部屋が空`);
          return;
        }
        const room = String(roomVal).trim();

        let startFormatted = '';
        let endFormatted = '';
        let isTfRow = false;

        if (tfMode && isTfMarker(startRaw)) {
          // TF行
          isTfRow = true;
          const duration = parseDurationMinutes(mapping.duration ? row[mapping.duration] : '');
          if (!duration || duration <= 0) {
            skipReasons.push(`行${idx + 2}: TF行だが占有時間が読み取れない (${row[mapping.duration]})`);
            return;
          }
          startFormatted = lastEndByRoom[room] || defaultStart;
          endFormatted = addMinutesToHHMM(startFormatted, duration);
          tfPlaced.push({
            row: idx + 2,
            room,
            label: String(startRaw).trim(),
            start: startFormatted,
            end: endFormatted,
            duration,
          });
        } else {
          // 通常行：start は時刻、end は CSV の end か duration から算出
          startFormatted = formatTimeString(startRaw);
          if (!startFormatted) {
            skipReasons.push(`行${idx + 2}: 開始時刻形式が不正 (${startRaw})`);
            return;
          }
          const endRaw = mapping.end ? row[mapping.end] : '';
          if (endRaw) {
            endFormatted = formatTimeString(endRaw);
          } else if (mapping.duration) {
            const dur = parseDurationMinutes(row[mapping.duration]);
            if (dur) endFormatted = addMinutesToHHMM(startFormatted, dur);
          }
          if (!endFormatted) {
            skipReasons.push(`行${idx + 2}: 終了時刻が決定できない (end=${endRaw} / duration=${mapping.duration ? row[mapping.duration] : '未指定'})`);
            return;
          }
        }

        // 部屋ポインタ更新
        lastEndByRoom[room] = endFormatted;

        const { skills, unmatched } = buildRequiredSkills(
          mapping.requiredSkills ? row[mapping.requiredSkills] : ''
        );
        unmatched.forEach((n) => unmatchedSkillSet.add(n));

        rowsToInsert.push({
          organization_id: currentOrganization.id,
          date: selectedDate.format("YYYY-MM-DD"),
          room,
          procedure: (mapping.procedure && row[mapping.procedure]) || "",
          department: (mapping.department && row[mapping.department]) || "",
          surgeon: (mapping.surgeon && row[mapping.surgeon]) || "",
          start_time: startFormatted,
          end_time: endFormatted,
          priority: mapPriority((mapping.priority && row[mapping.priority]) || ""),
          position: (mapping.position && row[mapping.position]) || "",
          anesthesia: (mapping.anesthesia && row[mapping.anesthesia]) || "",
          required_skills: skills,
          skill_condition: (mapping.skillCondition && row[mapping.skillCondition]) || "AND",
          imported_from_csv: true,
          _isTfRow: isTfRow, // フロント側集計用（DB送信前に削除）
        });
      });

      // _isTfRow フラグを DB ペイロードから除外
      const dbPayloadAll = rowsToInsert.map(({ _isTfRow, ...rest }) => rest);

      // 🔥 (date, room, start_time) で既存と被るものを除外（重複防止）
      //   start_time は DB から "HH:MM:SS" で返ることがあるので 先頭 5 文字に正規化
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const { data: existingRows } = await supabase
        .from('surgery_schedules')
        .select('room, start_time')
        .eq('organization_id', currentOrganization.id)
        .eq('date', dateStr);
      const existingKeys = new Set(
        (existingRows || []).map(
          (r) => `${r.room}|${String(r.start_time || '').substring(0, 5)}`
        )
      );
      const duplicateRows = [];
      const dbPayload = dbPayloadAll.filter((p, i) => {
        const key = `${p.room}|${String(p.start_time || '').substring(0, 5)}`;
        if (existingKeys.has(key)) {
          duplicateRows.push({ row: i, room: p.room, start_time: p.start_time });
          return false;
        }
        // 同 CSV 内で重複しているものも除外（同じ batch で room/start が重なる行）
        if (existingKeys.has(key)) return false;
        existingKeys.add(key);
        return true;
      });

      // 🔥 roomSettings.rooms に存在しない room 値を検出（UI に出ない原因になる）
      const validRoomIds = new Set(roomSettings.rooms.map((r) => r.id));
      const unknownRooms = new Map(); // room -> 件数
      dbPayloadAll.forEach((p) => {
        if (!validRoomIds.has(p.room)) {
          unknownRooms.set(p.room, (unknownRooms.get(p.room) || 0) + 1);
        }
      });

      const tfCount = rowsToInsert.filter((r) => r._isTfRow).length;
      const normalCount = rowsToInsert.length - tfCount;

      // 🔥 insert 前バリデーション: 未登録 room / マスタ未登録術式があれば、
      //   ユーザーに「このまま登録するかどうか」を確認する。
      //   未登録 room → UI に表示されない / 未登録術式 → スキル照合が効かない
      if (dbPayload.length > 0 && (unknownRooms.size > 0 || unmatchedSkillSet.size > 0)) {
        const lines = ['以下のデータがマスタ未登録です。このまま登録しますか？\n'];
        if (unknownRooms.size > 0) {
          lines.push(`【未登録の部屋】(${unknownRooms.size}種)`);
          Array.from(unknownRooms.entries()).forEach(([room, count]) => {
            lines.push(`  ・「${room}」(${count}件)`);
          });
          lines.push('  → 登録しても画面には表示されません。ルーム設定で部屋を追加するか、CSV を修正してください。\n');
        }
        if (unmatchedSkillSet.size > 0) {
          const arr = Array.from(unmatchedSkillSet);
          lines.push(`【マスタ未登録の術式】(${arr.length}種)`);
          arr.slice(0, 10).forEach((n) => lines.push(`  ・「${n}」`));
          if (arr.length > 10) lines.push(`  …他 ${arr.length - 10} 種`);
          lines.push('  → スキル条件による配置候補の絞り込みが効きません。\n');
        }
        lines.push(`OK: そのまま ${dbPayload.length} 件を登録 / キャンセル: 中止して CSV または設定を修正`);
        if (!window.confirm(lines.join('\n'))) {
          showNotification('インポートを中止しました。マスタを整えてから再実行してください', 'info');
          // 中止時も結果モーダルに警告は反映（ユーザーが何を直すべきか分かるように）
          const result = {
            success: 0,
            skipped: skipReasons,
            duplicates: duplicateRows,
            unknownRooms: Array.from(unknownRooms.entries()).map(([room, count]) => ({ room, count })),
            unmatchedSkills: Array.from(unmatchedSkillSet),
            tfPlaced,
            normalCount: 0,
            tfCount: 0,
            cancelled: true,
          };
          setCsvMappingModal((prev) => ({ ...prev, result }));
          return;
        }
      }

      if (dbPayload.length === 0) {
        const result = {
          success: 0,
          skipped: skipReasons,
          duplicates: duplicateRows,
          unknownRooms: Array.from(unknownRooms.entries()).map(([room, count]) => ({ room, count })),
          tfPlaced,
          normalCount: 0,
          tfCount: 0,
        };
        setCsvMappingModal((prev) => ({ ...prev, result }));
        const dupMsg = duplicateRows.length > 0 ? `重複 ${duplicateRows.length} 件` : '';
        showNotification(
          `登録できる行がありません${[dupMsg, skipReasons.length && `${skipReasons.length}件スキップ`]
            .filter(Boolean).map((s) => `（${s}）`).join('')}`,
          'error'
        );
        return;
      }

      const { error } = await supabase.from('surgery_schedules').insert(dbPayload);
      if (error) {
        console.error("CSV一括登録エラー:", error, dbPayload);
        await writeClientAudit({
          organizationId: currentOrganization?.id,
          actionType: 'create',
          targetTable: 'surgery_schedules',
          targetSummary: `手術 CSV 一括登録失敗 (${dbPayload.length} 件予定)`,
          success: false,
          errorMessage: error.message,
        });
        showNotification(`登録失敗: ${error.message}`, 'error');
        return;
      }

      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: 'create',
        targetTable: 'surgery_schedules',
        targetSummary: `手術 CSV 一括登録: ${dbPayload.length} 件`,
        changes: { count: dbPayload.length },
        success: true,
      });

      const result = {
        success: dbPayload.length,
        skipped: skipReasons,
        duplicates: duplicateRows,
        unknownRooms: Array.from(unknownRooms.entries()).map(([room, count]) => ({ room, count })),
        tfPlaced,
        normalCount,
        tfCount,
        unmatchedSkills: Array.from(unmatchedSkillSet),
      };
      setCsvMappingModal((prev) => ({ ...prev, result }));

      const unmatchedMsg = unmatchedSkillSet.size > 0
        ? `／マスタ未登録の術式 ${unmatchedSkillSet.size}件`
        : '';
      const duplicateMsg = duplicateRows.length > 0 ? `／重複スキップ ${duplicateRows.length}件` : '';
      const unknownRoomMsg = unknownRooms.size > 0
        ? `／⚠️未登録の部屋 ${unknownRooms.size}種 (${Array.from(unknownRooms.keys()).slice(0, 3).join(',')}${unknownRooms.size > 3 ? '…' : ''})`
        : '';
      showNotification(
        `${dbPayload.length}件登録しました${skipReasons.length ? `（${skipReasons.length}件スキップ）` : ''}${duplicateMsg}${unmatchedMsg}${unknownRoomMsg}`,
        (unmatchedSkillSet.size > 0 || unknownRooms.size > 0) ? 'warning' : 'success'
      );
      if (skipReasons.length > 0) console.warn('スキップ詳細:', skipReasons);
      if (duplicateRows.length > 0) console.warn('重複スキップ詳細:', duplicateRows);
      if (unknownRooms.size > 0) {
        console.warn(
          '⚠️ roomSettings.rooms に存在しない部屋がインポートされました（UI に表示されません）:',
          Array.from(unknownRooms.entries())
        );
      }

      await fetchSurgeryData();
      // 結果表示中はモーダルを開いたままにする（ユーザーが結果を確認してから閉じる）
    } catch (error) {
      console.error("CSVインポートエラー:", error);
      showNotification(`CSVデータのインポートに失敗しました: ${error.message}`, 'error');
    } finally {
      setLoadingData(false);
    }
  };

  // ==========================================
  // 外来人員要件
  // ==========================================
  const getOutpatientRequirement = (date) => {
    const dateStr = date.format("YYYY-MM-DD");
    const dayOfWeek = date.day();
    if (outpatientSettings.specialDates[dateStr]) {
      return { ...outpatientSettings.specialDates[dateStr], type: "special", source: `特別設定 (${dateStr})` };
    }
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      return { ...outpatientSettings.patterns.weekday, type: "weekday", source: "平日パターン" };
    }
    return { ...outpatientSettings.patterns.weekend, type: "weekend", source: "土日パターン" };
  };

  // ==========================================
  // 外来設定操作
  // ==========================================
  const addOutpatientPattern = () => {
    if (!newPatternName.trim()) { showNotification("パターン名を入力してください", "warning"); return; }
    if (outpatientSettings.patterns[newPatternName]) { showNotification("同じ名前のパターンが既に存在します", "warning"); return; }
    const newSettings = { ...outpatientSettings };
    newSettings.patterns[newPatternName] = { leader: 1, member: 2, note: "" };
    setOutpatientSettings(newSettings);
    setNewPatternName("");
  };

  const deleteOutpatientPattern = (patternName) => {
    if (Object.keys(outpatientSettings.patterns).length <= 1) { showNotification("少なくとも1つのパターンが必要です", "warning"); return; }
    const newSettings = { ...outpatientSettings };
    delete newSettings.patterns[patternName];
    setOutpatientSettings(newSettings);
  };

  const updateOutpatientPattern = (patternName, field, value) => {
    const newSettings = { ...outpatientSettings };
    if (!newSettings.patterns[patternName]) return;
    newSettings.patterns[patternName][field] = value;
    setOutpatientSettings(newSettings);
  };

  const addSpecialDate = () => {
    if (!specialDateInput.date) { showNotification("日付を選択してください", "warning"); return; }
    const newSettings = { ...outpatientSettings };
    newSettings.specialDates[specialDateInput.date] = {
      leader: parseInt(specialDateInput.leader) || 0,
      member: parseInt(specialDateInput.member) || 0,
      note: specialDateInput.note || ""
    };
    setOutpatientSettings(newSettings);
    setSpecialDateInput({ date: "", leader: 0, member: 0, note: "" });
  };

  const deleteSpecialDate = (dateStr) => {
    const newSettings = { ...outpatientSettings };
    delete newSettings.specialDates[dateStr];
    setOutpatientSettings(newSettings);
  };

  const addOutpatientRole = () => {
    const newSettings = { ...outpatientSettings };
    newSettings.roles.push({ id: `role_${Date.now()}`, name: "新しいロール", color: "#6B7280" });
    setOutpatientSettings(newSettings);
  };

  const updateOutpatientRole = (index, field, value) => {
    const newSettings = { ...outpatientSettings };
    if (newSettings.roles[index]) { newSettings.roles[index][field] = value; setOutpatientSettings(newSettings); }
  };

  const deleteOutpatientRole = (index) => {
    if (outpatientSettings.roles.length <= 1) { showNotification("少なくとも1つのロールが必要です", "warning"); return; }
    const newSettings = { ...outpatientSettings };
    newSettings.roles.splice(index, 1);
    setOutpatientSettings(newSettings);
  };

  // ==========================================
  // 手術室操作
  // ==========================================
  const addRoom = () => {
    const newRooms = [...roomSettings.rooms];
    newRooms.push({ id: `OR${newRooms.length + 1}`, name: `OR${newRooms.length + 1}` });
    setRoomSettings({ ...roomSettings, rooms: newRooms });
  };

  const removeRoom = (index) => {
    if (roomSettings.rooms.length <= 1) { showNotification("少なくとも1つの部屋が必要です", "warning"); return; }
    const newRooms = [...roomSettings.rooms];
    newRooms.splice(index, 1);
    setRoomSettings({ ...roomSettings, rooms: newRooms });
  };

  const updateRoomName = (index, newName) => {
    const newRooms = [...roomSettings.rooms];
    newRooms[index].name = newName;
    setRoomSettings({ ...roomSettings, rooms: newRooms });
  };

  // ==========================================
  // スキル操作
  // ==========================================
  const normalizeRequiredSkills = (skills) => {
    if (!skills || skills.length === 0) return [];
    if (typeof skills[0] === 'object') return skills;
    if (procedures.length === 0) return skills;
    return skills.map(skillName => {
      const procedure = procedures.find(p => p.name === skillName);
      return procedure ? { id: procedure.id, name: procedure.name } : { id: null, name: skillName };
    });
  };

  const addSkillToModal = (skillName) => {
    const procedure = procedures.find(p => p.name === skillName);
    if (!procedure) return;
    const isAlreadySelected = modalInfo.requiredSkills.some(
      skill => typeof skill === 'object' ? skill.id === procedure.id : skill === skillName
    );
    if (!isAlreadySelected) {
      setModalInfo({ ...modalInfo, requiredSkills: [...modalInfo.requiredSkills, { id: procedure.id, name: procedure.name }] });
    }
  };

  const removeSkillFromModal = (skillToRemove) => {
    setModalInfo({
      ...modalInfo,
      requiredSkills: modalInfo.requiredSkills.filter(skill => {
        if (typeof skill === 'object' && typeof skillToRemove === 'object') return skill.id !== skillToRemove.id;
        if (typeof skill === 'object') return skill.name !== skillToRemove;
        return skill !== skillToRemove;
      })
    });
  };

  const toggleSkillCondition = () => {
    setModalInfo({ ...modalInfo, skillCondition: modalInfo.skillCondition === "AND" ? "OR" : "AND" });
  };

  // ==========================================
  // CSVユーティリティ
  // ==========================================
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      // 🔥 # 始まりの行はコメント扱いでスキップ (テンプレ説明文を入れるため)
      comments: '#',
      complete: (results) => {
        const headers = results.meta.fields || [];
        const initialMapping = {
          room: headers.find(h => h.toLowerCase().includes('room') || h.toLowerCase().includes('部屋')) || '',
          procedure: headers.find(h => h.toLowerCase().includes('procedure') || h.toLowerCase().includes('術式')) || '',
          department: headers.find(h => h.toLowerCase().includes('department') || h.toLowerCase().includes('診療科')) || '',
          surgeon: headers.find(h => h.toLowerCase().includes('surgeon') || h.toLowerCase().includes('執刀医')) || '',
          start: headers.find(h => h.toLowerCase().includes('start') || h.toLowerCase().includes('開始')) || '',
          end: headers.find(h => h.toLowerCase().includes('end') || h.toLowerCase().includes('終了')) || '',
          duration: headers.find(h => {
            const c = h.toLowerCase();
            return c.includes('duration') || c.includes('占有時間') || c.includes('所要時間') || c === '時間';
          }) || '',
          priority: headers.find(h => h.toLowerCase().includes('priority')) || '',
          position: headers.find(h => h.toLowerCase().includes('position') || h.toLowerCase().includes('体位')) || '',
          anesthesia: headers.find(h => h.toLowerCase().includes('anesthesia') || h.toLowerCase().includes('麻酔')) || '',
          requiredSkills: headers.find(h => h.toLowerCase().includes('skills')) || '',
          skillCondition: headers.find(h => h.toLowerCase().includes('condition')) || '',
        };
        setCsvMappingModal({
          show: true,
          headers,
          mapping: initialMapping,
          data: results.data,
          preview: results.data.slice(0, 5),
          fileName: file.name,
          result: null,
        });
      },
      error: () => showNotification('CSVファイルの解析に失敗しました', 'error')
    });
    event.target.value = '';
  };

  // mode: 'normal' | 'tf' | 'auto'(現在の設定に従う)
  // 🔥 自病院の設定 (roomSettings.rooms / procedures) を反映したテンプレを生成する。
  //   - 部屋名は ScrubEdge 設定で登録された ID を使用 (完全一致が必須なため)
  //   - 全部屋分のサンプル行を入れて、ユーザーが部屋名の表記を一目で分かるように
  //   - 既存の術式マスタからサンプル術式を流用 (なければ汎用例)
  //   - 冒頭にコメント行 (# 始まり) で取込仕様を説明
  //     ※ Papa.parse 側に `comments: '#'` を設定済みなので取込時はスキップされる
  const downloadCSVTemplate = (mode = 'auto') => {
    const tfMode = mode === 'tf' || (mode === 'auto' && !!csvImportSettings.tfModeEnabled);
    const headers = tfMode
      ? ["room","procedure","department","surgeon","start","duration","priority","position","anesthesia","requiredSkills","skillCondition"]
      : ["room","procedure","department","surgeon","start","end","priority","position","anesthesia","requiredSkills","skillCondition"];

    // 🔥 ScrubEdge 登録部屋一覧 (なければデフォルト OR1-2)
    const registeredRooms = (roomSettings?.rooms || []).map(r => r.id || r.name).filter(Boolean);
    const sampleRooms = registeredRooms.length > 0 ? registeredRooms : ["OR1", "OR2"];

    // 🔥 術式マスタから先頭 3 件をサンプルに流用 (なければ汎用)
    const sampleProcsFromDb = (procedures || []).slice(0, 3).map(p => p.name).filter(Boolean);
    const procSamples = sampleProcsFromDb.length >= 2 ? sampleProcsFromDb : ["冠動脈バイパス術", "腹腔鏡下胆嚢摘出術", "胃癌手術"];

    // 🔥 各部屋に対してサンプル行を生成 (全部屋分 = ユーザーが部屋名表記を一目で分かる)
    const sampleData = sampleRooms.map((roomId, idx) => {
      const proc = procSamples[idx % procSamples.length];
      return tfMode
        ? { room: roomId, procedure: proc, department: "心臓血管外科", surgeon: "佐藤一郎",
            start: idx === 0 ? "09:00" : "TF1", duration: "2:30",
            priority: idx === 0 ? "normal" : "urgent", position: "仰臥位", anesthesia: "全身麻酔",
            requiredSkills: proc, skillCondition: "AND" }
        : { room: roomId, procedure: proc, department: "心臓血管外科", surgeon: "佐藤一郎",
            start: idx === 0 ? "09:00" : "10:30", end: idx === 0 ? "13:00" : "12:30",
            priority: idx === 0 ? "normal" : "urgent", position: "仰臥位", anesthesia: "全身麻酔",
            requiredSkills: proc, skillCondition: idx === 0 ? "AND" : "OR" };
    });

    // 🔥 1 行だけ短い案内 (詳細はヘルプページ参照)
    //   ※ Papa.parse の comments: '#' で取込時スキップされる
    let csvContent = `# 詳細はCSV取込のヘルプ参照\n`;
    csvContent += headers.join(",") + "\n";
    sampleData.forEach(row => {
      csvContent += headers.map(h => { const v = row[h]||""; return v.includes(",") ? `"${v}"` : v; }).join(",") + "\n";
    });
    const bom = '\uFEFF';  // Excel用BOM（文字化け防止）
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', tfMode ? '手術予定_TFモード_テンプレート.csv' : '手術予定_テンプレート.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTimeString = (timeStr) => {
    if (!timeStr) return "";
    if (/^\d{1,2}:\d{2}$/.test(timeStr)) { const [h,m] = timeStr.split(':'); return `${h.padStart(2,'0')}:${m}`; }
    if (/^\d{3,4}$/.test(timeStr)) { const p = timeStr.padStart(4,'0'); return `${p.substring(0,2)}:${p.substring(2,4)}`; }
    try { const parsed = dayjs(timeStr); if (parsed.isValid()) return parsed.format('HH:mm'); } catch(e) {}
    return "";
  };

  const mapPriority = (str) => {
    const s = str.toLowerCase();
    if (s.includes('emergency')||s.includes('緊急')||s.includes('超緊急')) return "emergency";
    if (s.includes('urgent')||s.includes('優先')) return "urgent";
    return "normal";
  };

  // ==========================================
  // セルクリック
  // ==========================================
  const handleCellClick = (room, quarter, surgery) => {
    if (mode === "request") {
      if (surgery) {
        setModalInfo({ ...surgery, requiredSkills: surgery.requiredSkills || [], skillCondition: surgery.skillCondition || "AND" });
      } else {
        const hour = Math.floor(quarter / 4);
        const minute = (quarter % 4) * 15;
        const start = `${String(hour).padStart(2,"0")}:${String(minute).padStart(2,"0")}`;
        const end = `${String(hour+1).padStart(2,"0")}:${String(minute).padStart(2,"0")}`;
        setModalInfo({ room, hour, start, end, surgeon: "", procedure: "", department: "", position: "", anesthesia: "", priority: "normal", requiredSkills: [], skillCondition: "AND" });
      }
    } else if (mode === "assignment" && surgery) {
      setShowStaffSelection({ surgery });
    } else if (mode === "info" && surgery) {
      setSelectedSurgeryForInfo(surgery);
    }
  };

  const getSurgeryColor = (surgery) => {
    if (!surgery) return {};
    if (surgery.priority === "emergency") return { backgroundColor: "rgba(239,68,68,0.15)", borderLeft: "4px solid rgb(239,68,68)" };
    if (surgery.priority === "urgent") return { backgroundColor: "rgba(245,158,11,0.15)", borderLeft: "4px solid rgb(245,158,11)" };
    return { backgroundColor: "rgba(59,130,246,0.15)", borderLeft: "4px solid rgb(59,130,246)" };
  };

  // ==========================================
  // 時間スロット
  // ==========================================
  const startHour = 0, endHour = 24;
  const timeSlots = Array.from({ length: (endHour - startHour) * 4 }, (_, i) => {
    const hour = Math.floor(i / 4) + startHour;
    const minute = (i % 4) * 15;
    return `${String(hour).padStart(2,"0")}:${String(minute).padStart(2,"0")}`;
  });

  const getCurrentTimePosition = () => {
    if (dayjs().format('YYYY-MM-DD') !== selectedDate.format('YYYY-MM-DD')) return null;
    const now = dayjs();
    const hour = now.hour(), minute = now.minute();
    if (hour < startHour || hour >= endHour) return null;
    const slotIndex = (hour - startHour) * 4 + Math.floor(minute / 15);
    return { slotIndex, positionInSlot: (minute % 15) / 15 };
  };

  const currentOutpatientReq = getOutpatientRequirement(selectedDate);

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-700">データを読み込み中...</p>
        </div>
      </div>
    );
  }

  // ==========================================
  // JSX（UIは元のままほぼ維持）
  // ==========================================
  return (
    <div className="bg-gray-50 min-h-screen pb-8">
      {notification.show && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-[9500] transform transition-all duration-500 ${notification.type==="error"?"bg-red-500":notification.type==="warning"?"bg-yellow-500":"bg-blue-500"} text-white`}>
          {notification.message}
        </div>
      )}

      <style jsx>{`
        @keyframes blinkingLine { 0%{opacity:1}50%{opacity:0.4}100%{opacity:1} }
        .current-time-line { position:absolute;top:0;bottom:0;width:2px;background-color:red;animation:blinkingLine 2s infinite;z-index:5; }
        .surgery-card-clean { border-radius:8px;padding:8px;box-shadow:0 2px 4px rgba(0,0,0,0.1);border:1px solid rgba(255,255,255,0.3);font-size:11px;line-height:1.2;height:100%;min-height:60px;display:flex;flex-direction:column;justify-content:space-between;overflow:hidden;transition:transform 0.2s,box-shadow 0.2s; }
        .surgery-card-clean:hover { transform:translateY(-1px);box-shadow:0 4px 8px rgba(0,0,0,0.15); }
        .surgery-title-clean { font-weight:bold;font-size:12px;color:#1e3a8a;margin-bottom:4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
        .surgery-time-clean { font-size:10px;color:#374151;margin-bottom:3px;font-weight:500; }
        .surgery-info-clean { display:flex;justify-content:space-between;align-items:center;margin-bottom:4px; }
        .surgery-department-clean { background:rgba(59,130,246,0.1);color:#1e40af;padding:2px 6px;border-radius:12px;font-size:9px;font-weight:500;border:1px solid rgba(59,130,246,0.2); }
        .surgery-surgeon-clean { font-size:9px;color:#4b5563;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:80px; }
        .surgery-staff-clean { display:flex;gap:4px;margin-top:4px; }
        .staff-badge-clean { display:flex;align-items:center;gap:2px;background:rgba(16,185,129,0.1);color:#047857;padding:2px 4px;border-radius:6px;font-size:8px;font-weight:500;border:1px solid rgba(16,185,129,0.2);flex:1;min-width:0; }
        .staff-badge-scrub { background:rgba(245,158,11,0.1);color:#92400e;border-color:rgba(245,158,11,0.2); }
        .staff-name-clean { overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
        .priority-emergency { background:linear-gradient(135deg,rgba(239,68,68,0.15),rgba(248,113,113,0.1));border-left:4px solid #ef4444; }
        .priority-urgent { background:linear-gradient(135deg,rgba(245,158,11,0.15),rgba(251,191,36,0.1));border-left:4px solid #f59e0b; }
        .priority-normal { background:linear-gradient(135deg,rgba(59,130,246,0.15),rgba(96,165,250,0.1));border-left:4px solid #3b82f6; }
      `}</style>

      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-6">
            <h1 className="text-2xl font-bold text-gray-900">📝 手術申し込み画面</h1>
            <div className="w-full lg:w-auto lg:min-w-[400px]">
              <EnhancedDatePicker selectedDate={selectedDate} onDateChange={setSelectedDate} />
            </div>
          </div>
        </div>

        {/* 🔥 日次役割割当 (リーダー等。 設定 → 日次役割マスタ で役割を定義)
            mode を問わず常に表示 (外来人員要件と同じ扱い) */}
        <div className="mb-6">
          <DailyRoleAssignmentPanel selectedDate={selectedDate} />
        </div>

        {/* 外来人員要件 */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg shadow-sm p-4 mb-6 border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-green-600 mr-2">🏥</span>
              <h3 className="text-lg font-medium text-gray-900">外来人員要件</h3>
            </div>
            <button onClick={() => setShowOutpatientSettingsModal(true)} className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm">⚙️ 設定</button>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded-md border">
              <div className="text-sm text-gray-600">{currentOutpatientReq.source}</div>
              <div className="mt-1 flex flex-wrap gap-2">
                {outpatientSettings.roles.map(role => (
                  <span key={role.id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: role.color+'20', color: role.color, border: `1px solid ${role.color}40` }}>
                    {role.name}: {currentOutpatientReq[role.id] || 0}人
                  </span>
                ))}
              </div>
              {currentOutpatientReq.note && <div className="mt-2 text-xs text-gray-500">{currentOutpatientReq.note}</div>}
            </div>
            <div className="bg-white p-3 rounded-md border"><div className="text-sm text-gray-600">実際の配置</div><div className="mt-1 text-sm text-gray-800">未実装</div></div>
            <div className="bg-white p-3 rounded-md border"><div className="text-sm text-gray-600">充足率</div><div className="mt-1 text-sm text-gray-800">未実装</div></div>
          </div>
        </div>

        <ExcludedStaffPanel selectedDate={selectedDate} />

        {/* モード選択 */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            {[['request','✏️ 手術申込モード'],['assignment','👥 人員配置モード'],['info','📋 情報収集モード']].map(([m,label]) => (
              <button key={m} onClick={() => setMode(m)} className={`px-6 py-2 rounded-md transition-all duration-200 ${mode===m?'bg-blue-600 text-white shadow-md':'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{label}</button>
            ))}
            <button onClick={() => setShowAutoAssignModal(true)} className="px-6 py-2 rounded-md bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md hover:shadow-lg transform hover:scale-105">🔧 強化版自動人員配置</button>
          </div>

          <div className="flex flex-wrap justify-center gap-3 pt-3 border-t border-gray-200">
            <button onClick={() => setShowRoomSettingsModal(true)} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 shadow-sm flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" /></svg>
              手術室設定
            </button>
            <button onClick={() => setShowOCRModal(true)} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 shadow-sm">📸 スケジュールをスキャン</button>
            <button onClick={saveAllAssignmentsToHistory} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 shadow-sm flex items-center gap-1">
              💾 配置履歴を保存
            </button>
            <button
              onClick={handlePublishToHome}
              disabled={publishingHome}
              className={`px-4 py-2 rounded-md shadow-sm flex items-center gap-1 ${
                publishingHome
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-rose-600 text-white hover:bg-rose-700'
              }`}
              title="その日の手術配置を全員のホーム画面に公開します"
            >
              {publishingHome ? '反映中...' : '📤 ホーム画面に反映'}
            </button>
            <div className="relative">
              <button onClick={() => setCsvImportMenuOpen(!csvImportMenuOpen)} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 shadow-sm flex items-center">
                CSV取込 <svg className="w-4 h-4 ml-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
              {csvImportMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-[9000] border border-gray-200">
                  <div className="py-1">
                    <button onClick={() => { setCsvImportMenuOpen(false); fileInputRef.current.click(); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">📂 CSVファイルを選択</button>
                    <div className="border-t border-gray-100 my-1" />
                    <div className="px-4 py-1 text-xs text-gray-400">テンプレート</div>
                    <button onClick={() => { setCsvImportMenuOpen(false); downloadCSVTemplate('normal'); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">📥 通常版テンプレート</button>
                    <button onClick={() => { setCsvImportMenuOpen(false); downloadCSVTemplate('tf'); }} className="w-full text-left px-4 py-2 text-sm text-violet-700 hover:bg-violet-50">📥 TFモード版テンプレート</button>
                    <div className="border-t border-gray-100 my-1" />
                    <button onClick={() => { setCsvImportMenuOpen(false); window.open('/surgery-request/csv-help', '_blank', 'noopener'); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">❓ CSV取込のヘルプ</button>
                  </div>
                </div>
              )}
            </div>
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".csv" style={{ display: 'none' }} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-3 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">現在時刻:</span>
            <span className="text-sm font-medium">{currentTime.format("HH:mm:ss")}</span>
          </div>
        </div>

        {/* スケジュール表 */}
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <div className="p-2 min-w-max" ref={tableRef}>
            <table className="w-full border-collapse relative">
              <thead>
                <tr>
                  <th className="w-24 px-2 py-3 bg-gray-100 text-left text-xs font-medium text-gray-600 uppercase tracking-wider sticky left-0 z-10">部屋</th>
                  {timeSlots.map((time, i) => {
                    const minute = i % 4 * 15;
                    return (
                      <th key={`time_header_${i}_${time.replace(':','_')}`} className={`w-12 px-1 py-2 text-xs font-medium text-gray-600 text-center ${minute===0?'border-l border-gray-300':''}`}>
                        {minute===0 ? time.split(':')[0]+':00' : ''}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {roomSettings.rooms.map((room, roomIndex) => (
                  <tr key={`room_row_${room.id}_${roomIndex}`} className={roomIndex%2===0?'bg-gray-50':'bg-white'}>
                    <td className="px-2 py-3 text-sm font-medium text-gray-800 sticky left-0 z-10 bg-inherit border-b border-gray-200">{room.name}</td>
                    {timeSlots.map((timeSlot, quarterIndex) => {
                      const quarter = quarterIndex + (startHour * 4);
                      const hour = parseInt(timeSlot.split(':')[0]);
                      const minute = parseInt(timeSlot.split(':')[1]);
                      const currentTimeStr = `${String(hour).padStart(2,"0")}:${String(minute).padStart(2,"0")}`;
                      const cellKey = `timeslot_${room.id}_${quarterIndex}_${roomIndex}_${hour}_${minute}`;

                      const surgery = Object.values(surgeryData).find(s =>
                        s.room === room.id && s.start === currentTimeStr && s.date === selectedDate.format("YYYY-MM-DD")
                      );

                      const timePosition = getCurrentTimePosition();
                      const isCurrentTimeSlot = timePosition && timePosition.slotIndex === quarterIndex;

                      const isCovered = Object.values(surgeryData).some(s => {
                        if (s.room !== room.id) return false;
                        const start = dayjs(`${selectedDate.format("YYYY-MM-DD")} ${s.start}`);
                        const end = dayjs(`${selectedDate.format("YYYY-MM-DD")} ${s.end}`);
                        const current = dayjs(`${selectedDate.format("YYYY-MM-DD")} ${currentTimeStr}`);
                        return current.isAfter(start) && current.isBefore(end);
                      });

                      if (isCovered && !surgery) return null;

                      if (surgery) {
                        const sH = parseInt(surgery.start.split(":")[0]), sM = parseInt(surgery.start.split(":")[1]);
                        const eH = parseInt(surgery.end.split(":")[0]), eM = parseInt(surgery.end.split(":")[1]);
                        const span = ((eH*60+eM)-(sH*60+sM))/15;
                        const priorityClass = surgery.priority==="emergency"?"priority-emergency":surgery.priority==="urgent"?"priority-urgent":"priority-normal";

                        return (
                          <td key={cellKey} colSpan={span} className="relative p-0 border-b border-gray-200">
                            {isCurrentTimeSlot && <div className="current-time-line" style={{ left: `${timePosition.positionInSlot*100}%` }}></div>}
                            <DraggableSurgeryBar surgery={surgery} quarter={quarter} room={room.id} durationMinutes={surgery.durationMinutes} onClick={() => handleCellClick(room.id, quarter, surgery)} onDropSuccess={refreshData} className="h-full w-full p-1 relative cursor-pointer">
                              <div className={`surgery-card-clean ${priorityClass}`}>
                                <div>
                                  <div className="surgery-title-clean">{surgery.procedure || "予定手術"}</div>
                                  <div className="surgery-time-clean">{surgery.start} - {surgery.end}</div>
                                  <div className="surgery-info-clean">
                                    {surgery.department && <div className="surgery-department-clean">{surgery.department.length>6?surgery.department.substring(0,6)+'..':surgery.department}</div>}
                                    <div className="surgery-surgeon-clean">{surgery.surgeon || "未定"}</div>
                                  </div>
                                </div>
                                <div className="surgery-staff-clean">
                                  <div className="staff-badge-clean staff-badge-scrub">
                                    <span>器</span>
                                    <div className="staff-name-clean" style={{fontSize:'10px'}}>
                                      {(() => {
                                        const val = surgery.scrub;
                                        if (!val) return "未定";
                                        const arr = Array.isArray(val) ? val : [val];
                                        return arr.map(s => s.name ? (s.name.includes(' ') ? s.name.split(' ')[0] : s.name.substring(0,4)) : '').filter(Boolean).join('・') || "未定";
                                      })()}
                                    </div>
                                  </div>
                                  <div className="staff-badge-clean">
                                    <span>外</span>
                                    <div className="staff-name-clean" style={{fontSize:'10px'}}>
                                      {(() => {
                                        const val = surgery.circulating;
                                        if (!val) return "未定";
                                        const arr = Array.isArray(val) ? val : [val];
                                        return arr.map(s => s.name ? (s.name.includes(' ') ? s.name.split(' ')[0] : s.name.substring(0,4)) : '').filter(Boolean).join('・') || "未定";
                                      })()}
                                    </div>
                                  </div>
                                </div>
                                {surgery.tags && surgery.tags.length > 0 && (
                                  <div style={{ display:'flex',gap:'4px',marginTop:'6px',flexWrap:'wrap',paddingLeft:'4px' }}>
                                    {surgery.tags.slice(0,2).map(tag => (
                                      <span key={tag.id} style={{ fontSize:'10px',padding:'2px 6px',borderRadius:'8px',fontWeight:'600',backgroundColor:getTagColor(tag.color).bg,color:getTagColor(tag.color).text,whiteSpace:'nowrap' }}>{tag.name}</span>
                                    ))}
                                    {surgery.tags.length>2 && <span style={{ fontSize:'10px',padding:'2px 6px',borderRadius:'8px',backgroundColor:'#e5e7eb',color:'#6b7280',fontWeight:'600' }}>+{surgery.tags.length-2}</span>}
                                  </div>
                                )}
                                <div style={{ display:'flex',flexDirection:'column',gap:'2px',marginTop:'2px' }}>
  {(() => {
    const hasScrub = surgery.scrub && (Array.isArray(surgery.scrub) ? surgery.scrub.length > 0 : true);
    const hasCirc = surgery.circulating && (Array.isArray(surgery.circulating) ? surgery.circulating.length > 0 : true);

    // 同日の他手術と時間が重複しているスタッフがいるか確認
    const toMin = (t) => { if (!t) return 0; const [h,m] = t.split(':').map(Number); return h*60+m; };
    const thisStart = toMin(surgery.start);
    const thisEnd = toMin(surgery.end);
    const allAssigned = [
      ...(Array.isArray(surgery.scrub) ? surgery.scrub : surgery.scrub ? [surgery.scrub] : []),
      ...(Array.isArray(surgery.circulating) ? surgery.circulating : surgery.circulating ? [surgery.circulating] : []),
    ];
    const overlapWarnings = [];
    allAssigned.forEach(person => {
      if (!person?.employeeId) return;
      Object.values(surgeryData).forEach(other => {
        if (other.docId === surgery.docId) return;
        const oStart = toMin(other.start);
        const oEnd = toMin(other.end);
        if (oStart >= thisEnd || oEnd <= thisStart) return; // 時間重複なし
        const otherAll = [
          ...(Array.isArray(other.scrub) ? other.scrub : other.scrub ? [other.scrub] : []),
          ...(Array.isArray(other.circulating) ? other.circulating : other.circulating ? [other.circulating] : []),
        ];
        if (otherAll.some(p => p?.employeeId === person.employeeId)) {
          const name = person.name?.split(' ')[0] || person.name?.substring(0,3) || '';
          if (name && !overlapWarnings.includes(name)) overlapWarnings.push(name);
        }
      });
    });

    return (
      <>
        <div style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
          {!hasScrub || !hasCirc
            ? <span style={{color:'#ef4444',fontSize:'10px'}}>⚠️ 配置未完了</span>
            : <span style={{color:'#10b981',fontSize:'10px'}}>✓ 配置完了</span>
          }
        </div>
        {overlapWarnings.length > 0 && (
          <div style={{
            fontSize:'9px', padding:'1px 4px', borderRadius:'4px',
            backgroundColor:'rgba(239,68,68,0.12)',
            color:'#b91c1c', border:'1px solid rgba(239,68,68,0.3)',
            textAlign:'center', lineHeight:'1.4',
          }}>
            🔴 重複: {overlapWarnings.join('・')}
          </div>
        )}
      </>
    );
  })()}
</div>
                              </div>
                            </DraggableSurgeryBar>
                          </td>
                        );
                      }

                      return (
                        <td key={cellKey} className={`relative border-b border-gray-200 ${minute===0?'border-l border-gray-300':''} h-12 min-w-[3rem] hover:bg-blue-50 cursor-pointer`} onClick={() => handleCellClick(room.id, quarter, null)}>
                          {isCurrentTimeSlot && <div className="current-time-line" style={{ left: `${timePosition.positionInSlot*100}%` }}></div>}
                          <DropCell room={room.id} currentTime={currentTimeStr} onDrop={async (item) => {
                            if (!item.docId) return;
                            try {
                              const newEnd = dayjs(`2024-01-01 ${currentTimeStr}`).add(item.durationMinutes,"minute").format("HH:mm");
                              const { error } = await supabase.from('surgery_schedules').update({ room: room.id, start_time: currentTimeStr, end_time: newEnd, updated_at: new Date().toISOString() }).eq('id', item.docId);
                              if (error) throw error;
                              showNotification("手術予定を移動しました","success");
                              refreshData();
                            } catch(error) {
                              console.error("移動エラー:", error);
                              showNotification("手術予定の移動に失敗しました","error");
                            }
                          }} className="w-full h-full" />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* リアルタイム人員状況 */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">📊 リアルタイム人員状況（今日の日勤者のみ）</h3>
            <div className="text-sm text-gray-500">最終更新: {currentTime.format("HH:mm:ss")}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-3">全体状況</h4>
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-sm text-blue-700">日勤スタッフ数:</span><span className="font-medium text-blue-900">{availableStaffInfo.total}名</span></div>
                <div className="flex justify-between"><span className="text-sm text-blue-700">手術中:</span><span className="font-medium text-red-600">{availableStaffInfo.busy.length}名</span></div>
                <div className="flex justify-between"><span className="text-sm text-blue-700">空き:</span><span className="font-medium text-green-600">{availableStaffInfo.available.length}名</span></div>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h4 className="font-medium text-green-900 mb-3">🟢 空き日勤スタッフ ({availableStaffInfo.available.length}名)</h4>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {availableStaffInfo.available.length > 0 ? availableStaffInfo.available.map((staff, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-green-800 font-medium">{staff.displayName}</span>
                    <span className="text-green-600 text-xs">{staff.experience}年目</span>
                  </div>
                )) : <div className="text-sm text-green-600 italic">全日勤スタッフが配置済みです</div>}
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <h4 className="font-medium text-red-900 mb-3">🔴 手術中日勤スタッフ ({availableStaffInfo.busy.length}名)</h4>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {availableStaffInfo.busy.length > 0 ? availableStaffInfo.busy.map((staff, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-red-800 font-medium">{staff.displayName}</span>
                    <span className="text-red-600 text-xs">{staff.experience}年目</span>
                  </div>
                )) : <div className="text-sm text-red-600 italic">手術中の日勤スタッフはいません</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 bg-white rounded-lg shadow-sm p-4">
          <div className="text-sm font-medium text-gray-700 mb-2">凡例：</div>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center"><div className="w-4 h-4 bg-blue-100 border-l-4 border-blue-500 mr-2"></div><span className="text-sm">通常手術</span></div>
            <div className="flex items-center"><div className="w-4 h-4 bg-yellow-100 border-l-4 border-yellow-500 mr-2"></div><span className="text-sm">緊急優先</span></div>
            <div className="flex items-center"><div className="w-4 h-4 bg-red-100 border-l-4 border-red-500 mr-2"></div><span className="text-sm">超緊急</span></div>
          </div>
          <div className="mt-2 text-xs text-gray-500">※ドラッグ&ドロップで手術予定を移動できます</div>
        </div>
      </main>

      {/* 外来設定モーダル */}
      {showOutpatientSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] flex flex-col" onClick={e=>e.stopPropagation()}>
            <div className="bg-green-600 text-white px-6 py-4 flex justify-between items-center flex-shrink-0">
              <h3 className="text-lg font-medium">外来人員設定</h3>
              <button onClick={() => setShowOutpatientSettingsModal(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
            </div>
            <div className="border-b border-gray-200 px-6">
              <nav className="flex space-x-8">
                {[['patterns','📅 曜日パターン'],['special','📆 特別日設定'],['roles','👥 ロール設定']].map(([tab,label]) => (
                  <button key={tab} onClick={() => setOutpatientActiveTab(tab)} className={`py-3 px-1 border-b-2 font-medium text-sm ${outpatientActiveTab===tab?'border-green-500 text-green-600':'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
                ))}
              </nav>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {outpatientActiveTab === "patterns" && (
                <div className="space-y-4">
                  {Object.entries(outpatientSettings.patterns).map(([patternName, pattern]) => (
                    <div key={patternName} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-medium">{patternName==="weekday"?"平日パターン":patternName==="weekend"?"土日パターン":patternName}</h5>
                        {!["weekday","weekend"].includes(patternName) && <button onClick={() => deleteOutpatientPattern(patternName)} className="text-red-600 text-sm">削除</button>}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {outpatientSettings.roles.map(role => (
                          <div key={role.id}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{role.name}</label>
                            <input type="number" min="0" value={pattern[role.id]||0} onChange={e => updateOutpatientPattern(patternName, role.id, parseInt(e.target.value)||0)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" />
                          </div>
                        ))}
                      </div>
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">メモ</label>
                        <input type="text" value={pattern.note||""} onChange={e => updateOutpatientPattern(patternName, "note", e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </div>
                    </div>
                  ))}
                  <div className="mt-6 border-t pt-6">
                    <h5 className="font-medium mb-3">新しいパターンを追加</h5>
                    <div className="flex gap-3">
                      <input type="text" value={newPatternName} onChange={e => setNewPatternName(e.target.value)} placeholder="パターン名を入力" className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      <button onClick={addOutpatientPattern} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">追加</button>
                    </div>
                  </div>
                </div>
              )}
              {outpatientActiveTab === "special" && (
                <div className="space-y-6">
                  <div className="border border-gray-200 rounded-lg p-4 mb-6">
                    <h5 className="font-medium mb-3">新しい特別日を追加</h5>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">日付</label><input type="date" value={specialDateInput.date} onChange={e => setSpecialDateInput({...specialDateInput,date:e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" /></div>
                      {outpatientSettings.roles.map(role => (
                        <div key={role.id}><label className="block text-sm font-medium text-gray-700 mb-1">{role.name}</label><input type="number" min="0" value={specialDateInput[role.id]||0} onChange={e => setSpecialDateInput({...specialDateInput,[role.id]:parseInt(e.target.value)||0})} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" /></div>
                      ))}
                    </div>
                    <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-1">メモ</label><input type="text" value={specialDateInput.note} onChange={e => setSpecialDateInput({...specialDateInput,note:e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="応援あり、研修日など..." /></div>
                    <button onClick={addSpecialDate} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">特別日を追加</button>
                  </div>
                  <div className="space-y-3">
                    <h5 className="font-medium">設定済み特別日</h5>
                    {Object.keys(outpatientSettings.specialDates).length===0 ? <p className="text-gray-500 text-sm">特別日設定はありません</p> :
                      Object.entries(outpatientSettings.specialDates).map(([dateStr,setting]) => (
                        <div key={dateStr} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between mb-2">
                            <h6 className="font-medium">{dateStr} ({dayjs(dateStr).format("ddd")})</h6>
                            <button onClick={() => deleteSpecialDate(dateStr)} className="text-red-600 text-sm">削除</button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {outpatientSettings.roles.map(role => (
                              <span key={role.id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor:role.color+'20',color:role.color,border:`1px solid ${role.color}40` }}>{role.name}: {setting[role.id]||0}人</span>
                            ))}
                          </div>
                          {setting.note && <p className="text-sm text-gray-600 mt-1">{setting.note}</p>}
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
              {outpatientActiveTab === "roles" && (
                <div className="space-y-4">
                  {outpatientSettings.roles.map((role, index) => (
                    <div key={role.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div><label className="block text-sm font-medium text-gray-700 mb-1">ロール名</label><input type="text" value={role.name} onChange={e => updateOutpatientRole(index,"name",e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" /></div>
                        <div><label className="block text-sm font-medium text-gray-700 mb-1">色</label><input type="color" value={role.color} onChange={e => updateOutpatientRole(index,"color",e.target.value)} className="w-full h-10 border border-gray-300 rounded-md" /></div>
                        <div className="flex items-end">{outpatientSettings.roles.length>1 && <button onClick={() => deleteOutpatientRole(index)} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">削除</button>}</div>
                      </div>
                    </div>
                  ))}
                  <button onClick={addOutpatientRole} className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">ロールを追加</button>
                </div>
              )}
            </div>
            <div className="flex-shrink-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button onClick={() => setShowOutpatientSettingsModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">キャンセル</button>
              <button onClick={saveOutpatientSettings} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* OCRモーダル */}
      {showOCRModal && (
        <SurgeryOCRScanner
          onSuccess={(surgeries) => { showNotification(`${surgeries.length}件スキャンしました`,"success"); setShowOCRModal(false); refreshData(); }}
          onClose={() => setShowOCRModal(false)}
        />
      )}

      {/* 手術予定モーダル */}
      {modalInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col" onClick={e=>e.stopPropagation()}>
            <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center flex-shrink-0">
              <h3 className="text-lg font-medium">{modalInfo.docId?"手術予定の編集":"新規手術予定"}</h3>
              <button onClick={() => { setModalInfo(null); setActiveSkillDept("all"); setSkillSearchKeyword(""); }} className="text-white hover:text-gray-200 text-2xl">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">手術室</label>
                  <select className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" value={modalInfo.room||""} onChange={e => setModalInfo({...modalInfo,room:e.target.value})}>
                    {roomSettings.rooms.map(room => <option key={room.id} value={room.id}>{room.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">優先度</label>
                  <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={modalInfo.priority||"normal"} onChange={e => setModalInfo({...modalInfo,priority:e.target.value})}>
                    <option value="normal">通常</option>
                    <option value="urgent">緊急優先</option>
                    <option value="emergency">超緊急</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">診療科</label>
                <select className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" value={modalInfo.department||""} onChange={e => setModalInfo({...modalInfo,department:e.target.value})}>
                  <option value="">-- 選択してください --</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.name}>{dept.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">術式</label>
                <input className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="術式" value={modalInfo.procedure||""} onChange={e => setModalInfo({...modalInfo,procedure:e.target.value})} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">執刀医</label>
                  <input className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="執刀医" value={modalInfo.surgeon||""} onChange={e => setModalInfo({...modalInfo,surgeon:e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">開始時間</label>
                    <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={modalInfo.start||""} onChange={e => setModalInfo({...modalInfo,start:e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">終了時間</label>
                    <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={modalInfo.end||""} onChange={e => setModalInfo({...modalInfo,end:e.target.value})} />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">体位</label>
                  <input className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="体位" value={modalInfo.position||""} onChange={e => setModalInfo({...modalInfo,position:e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">麻酔方法</label>
                  <input className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="麻酔方法" value={modalInfo.anesthesia||""} onChange={e => setModalInfo({...modalInfo,anesthesia:e.target.value})} />
                </div>
              </div>

              {/* スキル選択 */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">必要術式・スキル設定</h4>
                  {modalInfo.requiredSkills && modalInfo.requiredSkills.length > 0 && (
                    <button onClick={toggleSkillCondition} className={`px-3 py-1 rounded-full text-xs font-medium ${modalInfo.skillCondition==="AND"?"bg-blue-100 text-blue-800":"bg-green-100 text-green-800"}`}>
                      {modalInfo.skillCondition==="AND"?"すべて必要(AND)":"いずれか必要(OR)"}
                    </button>
                  )}
                </div>
                {modalInfo.requiredSkills && modalInfo.requiredSkills.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">選択済み術式</label>
                    <div className="flex flex-wrap gap-2">
                      {modalInfo.requiredSkills.map((skill, index) => {
                        const skillName = typeof skill==='object' ? skill.name : skill;
                        const skillId = typeof skill==='object' ? skill.id : null;
                        return (
                          <span key={skillId||index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {skillName}
                            <button onClick={() => removeSkillFromModal(skill)} className="ml-2 text-blue-600 hover:text-blue-800">✕</button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">術式を選択</label>
                  <div className="border-b border-gray-200 mb-4">
                    <nav className="flex space-x-6 overflow-x-auto">
                      <button onClick={() => setActiveSkillDept("all")} className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${activeSkillDept==="all"?"border-blue-500 text-blue-600":"border-transparent text-gray-500"}`}>すべて</button>
                      {Object.keys(skillsByDepartment).map(dept => (
                        <button key={dept} onClick={() => setActiveSkillDept(dept)} className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${activeSkillDept===dept?"border-blue-500 text-blue-600":"border-transparent text-gray-500"}`}>{dept}</button>
                      ))}
                    </nav>
                  </div>
                  <input type="text" placeholder="術式を検索..." value={skillSearchKeyword} onChange={e => setSkillSearchKeyword(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3" />
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {(() => {
                      let procs = activeSkillDept==="all" ? availableSkills : (skillsByDepartment[activeSkillDept]||[]);
                      if (skillSearchKeyword.trim()) procs = procs.filter(s => s.toLowerCase().includes(skillSearchKeyword.toLowerCase()));
                      const selectedNames = modalInfo.requiredSkills?.map(s => typeof s==='object' ? s.name : s) || [];
                      procs = procs.filter(s => !selectedNames.includes(s));
                      return procs.map((skill, index) => (
                        <button key={index} onClick={() => addSkillToModal(skill)} className="text-left px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-blue-50 hover:border-blue-500">{skill}</button>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 bg-gray-50 px-6 py-4 flex justify-end items-center space-x-3 flex-wrap gap-y-2">
              {modalInfo.docId && modalInfo.completed_at && (
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded font-medium">
                  ✓ 完了済み（{new Date(modalInfo.completed_at).toLocaleString('ja-JP')}）
                </span>
              )}
              {/* 🔥 「完了」ボタンは廃止: 編集内容を保存せず閉じる副作用があり、 キャンセル
                  と機能が重複して見えるとの指摘のため。 指導通知の手動送信機能が再度必要なら
                  「保存して完了」のような統合ボタンで再導入する。 */}
              {modalInfo.docId && (
                <button onClick={() => { if(window.confirm("この手術予定を削除しますか？")) saveSurgery(modalInfo,true); }} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">削除</button>
              )}
              <button onClick={() => { setModalInfo(null); setActiveSkillDept("all"); setSkillSearchKeyword(""); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">キャンセル</button>
              <button onClick={() => saveSurgery(modalInfo)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* スタッフ選択モーダル */}
      {showStaffSelection && (
        <StaffSelectionModal
          isOpen={true}
          surgery={showStaffSelection.surgery}
          selectedDate={selectedDate.format('YYYY-MM-DD')}
          onClose={() => setShowStaffSelection(null)}
          onAssign={handleStaffSelect}
        />
      )}

      {/* 手術室設定モーダル */}
      {showRoomSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col" onClick={e=>e.stopPropagation()}>
            <div className="bg-indigo-600 text-white px-6 py-4 flex justify-between items-center flex-shrink-0">
              <h3 className="text-lg font-medium">手術室設定</h3>
              <button onClick={() => setShowRoomSettingsModal(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
                {roomSettings.rooms.map((room, index) => (
                  <div key={`room_setting_${room.id}_${index}`} className="flex items-center space-x-2">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-sm font-medium text-indigo-700">{index+1}</div>
                    <input type="text" value={room.name} onChange={e => updateRoomName(index,e.target.value)} className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    <button onClick={() => removeRoom(index)} className="p-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={addRoom} className="mt-4 w-full py-3 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
                手術室を追加
              </button>
            </div>
            <div className="flex-shrink-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button onClick={() => setShowRoomSettingsModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">キャンセル</button>
              <button onClick={saveRoomSettings} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* CSVマッピングモーダル */}
      {csvMappingModal.show && (() => {
        const tfMode = !!csvImportSettings.tfModeEnabled;
        const mappingFields = tfMode
          ? [
              ['room', '手術室 *', '部屋名（例: OR1）'],
              ['start', '開始時間 / TF *', '"09:00" など時刻、もしくは "TF" / "TF1" / "TF2"'],
              ['duration', '占有時間 *', '"1:30" / "90" (分) / "1時間30分" / "1h30m" など'],
              ['procedure', '術式', ''],
              ['department', '診療科', ''],
              ['surgeon', '執刀医', ''],
              ['end', '終了時間', '通常行で省略可（占有時間から算出）'],
              ['priority', '優先度', 'emergency / urgent / normal'],
              ['position', '体位', ''],
              ['anesthesia', '麻酔方法', ''],
              ['requiredSkills', '必要スキル', 'カンマ区切り、術式マスタと一致でスキル照合可'],
              ['skillCondition', 'スキル条件', 'AND または OR（既定: AND）'],
            ]
          : [
              ['room', '手術室 *', '部屋名（例: OR1）'],
              ['start', '開始時間 *', '"09:00" など'],
              ['end', '終了時間 *', '"13:00" など'],
              ['procedure', '術式', ''],
              ['department', '診療科', ''],
              ['surgeon', '執刀医', ''],
              ['duration', '占有時間', 'end が空のときの fallback として使用可'],
              ['priority', '優先度', 'emergency / urgent / normal'],
              ['position', '体位', ''],
              ['anesthesia', '麻酔方法', ''],
              ['requiredSkills', '必要スキル', 'カンマ区切り、術式マスタと一致でスキル照合可'],
              ['skillCondition', 'スキル条件', 'AND または OR（既定: AND）'],
            ];
        const requiredKeys = tfMode ? ['room','start','duration'] : ['room','start','end'];
        const canImport = requiredKeys.every(k => csvMappingModal.mapping?.[k]);
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[10000] p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full overflow-hidden my-8" onClick={e=>e.stopPropagation()}>
            <div className="bg-teal-600 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium">📋 手術予定 CSV インポート</h3>
                <p className="text-teal-100 text-xs mt-0.5">{selectedDate.format('YYYY年M月D日')} の予定として登録されます</p>
              </div>
              <button onClick={() => setCsvMappingModal({...csvMappingModal,show:false,result:null})} className="text-white hover:text-gray-200 text-xl">✕</button>
            </div>
            <div className="p-6 max-h-[80vh] overflow-y-auto space-y-5">

              {/* モード切替＆既定開始時刻 */}
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-violet-900 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tfMode}
                        onChange={(e) => persistCsvImportSettings({ ...csvImportSettings, tfModeEnabled: e.target.checked })}
                        className="w-4 h-4"
                      />
                      🕒 TF（タイムフリー）モードを使う
                    </label>
                    <p className="text-xs text-violet-700 mt-1">
                      開始時刻欄に「TF / TF1 / TF2」と書かれた手術は、<strong>同じ部屋の直前の手術の終了時刻に続けて</strong>配置されます（前が無ければ既定開始時刻）。
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-violet-900 font-medium">既定の1日開始時刻:</span>
                    <input
                      type="time"
                      value={csvImportSettings.defaultStartTime || '09:00'}
                      onChange={(e) => persistCsvImportSettings({ ...csvImportSettings, defaultStartTime: e.target.value || '09:00' })}
                      className="border border-violet-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* ガイド */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-2">
                <p className="font-semibold">📖 使い方ガイド</p>
                <ol className="list-decimal list-inside space-y-1 text-xs text-blue-800">
                  <li>必要に応じて上の「TFモード」を切替（施設仕様に合わせて）</li>
                  <li>下記「カラムマッピング」で、CSVの各列がどの項目に当たるかを確認・修正</li>
                  <li>「テンプレートをダウンロード」で雛形を入手できます（現在のモードに応じた列構成）</li>
                  <li>「インポート実行」で {selectedDate.format('M月D日')} の手術予定として登録されます</li>
                </ol>
                {tfMode && (
                  <div className="mt-2 p-2 bg-violet-50 border border-violet-200 rounded text-xs text-violet-900">
                    <strong>例:</strong> OR1 で 9:00 / 占有3時間 の手術の次に「TF1 / 占有1時間」を入れると、12:00〜13:00 で自動配置されます。
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => downloadCSVTemplate('normal')}
                    className="text-xs text-blue-700 hover:underline"
                  >
                    📥 通常版テンプレートをダウンロード
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadCSVTemplate('tf')}
                    className="text-xs text-violet-700 hover:underline"
                  >
                    📥 TFモード版テンプレートをダウンロード
                  </button>
                </div>
              </div>

              {/* ファイル情報 */}
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                <div className="text-sm text-gray-700"><span className="font-medium">ファイル:</span> {csvMappingModal.fileName}</div>
                <div className="text-xs text-gray-500">{csvMappingModal.data?.length || 0} 行</div>
              </div>

              {/* プレビュー */}
              {csvMappingModal.preview?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">プレビュー（先頭5行）</p>
                  <div className="overflow-x-auto border border-gray-200 rounded text-xs">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          {csvMappingModal.headers.map(h => (
                            <th key={h} className="px-2 py-1.5 text-left font-medium text-gray-600 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvMappingModal.preview.map((row, i) => (
                          <tr key={i} className="border-t">
                            {csvMappingModal.headers.map(h => (
                              <td key={h} className="px-2 py-1 text-gray-700 whitespace-nowrap">{row[h]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* カラムマッピング */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">カラムマッピング</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {mappingFields.map(([field, label, hint]) => (
                    <div key={field}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                      <select className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" value={csvMappingModal.mapping[field]||""} onChange={e => setCsvMappingModal({...csvMappingModal,mapping:{...csvMappingModal.mapping,[field]:e.target.value}})}>
                        <option value="">-- 選択してください --</option>
                        {csvMappingModal.headers.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* 結果 */}
              {csvMappingModal.result && (
                <div className={`rounded-lg p-4 border ${csvMappingModal.result.success > 0 ? 'bg-green-50 border-green-200' : csvMappingModal.result.cancelled ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
                  {csvMappingModal.result.success > 0 ? (
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <span className="text-base">✅</span>
                      <span className="font-semibold">{csvMappingModal.result.success}件 を登録しました</span>
                      <span className="text-xs text-green-600">（通常 {csvMappingModal.result.normalCount} 件 / TF {csvMappingModal.result.tfCount} 件）</span>
                    </div>
                  ) : csvMappingModal.result.cancelled ? (
                    <div className="text-amber-700 font-semibold mb-2">⛔ インポートを中止しました（マスタ未登録の警告のため）</div>
                  ) : (
                    <div className="text-red-700 font-semibold mb-2">登録できた行がありませんでした</div>
                  )}
                  {csvMappingModal.result.tfPlaced?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-violet-800 mb-1">🕒 TF行の配置結果</p>
                      <div className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded p-2 max-h-32 overflow-y-auto space-y-0.5">
                        {csvMappingModal.result.tfPlaced.map((t, i) => (
                          <div key={i}>
                            行{t.row} / {t.room} / {t.label}: <strong>{t.start} 〜 {t.end}</strong>（占有 {t.duration} 分）
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {csvMappingModal.result.skipped?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-amber-800 mb-1">⚠️ スキップ {csvMappingModal.result.skipped.length} 件</p>
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 max-h-24 overflow-y-auto space-y-0.5">
                        {csvMappingModal.result.skipped.map((s, i) => <div key={i}>{s}</div>)}
                      </div>
                    </div>
                  )}
                  {csvMappingModal.result.duplicates?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-blue-800 mb-1">
                        🔁 重複スキップ {csvMappingModal.result.duplicates.length} 件（同じ日・部屋・開始時刻が既に登録済み）
                      </p>
                      <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2 max-h-24 overflow-y-auto space-y-0.5">
                        {csvMappingModal.result.duplicates.slice(0, 10).map((d, i) => (
                          <div key={i}>部屋 {d.room} / 開始 {String(d.start_time).substring(0, 5)}</div>
                        ))}
                        {csvMappingModal.result.duplicates.length > 10 && (
                          <div>…他{csvMappingModal.result.duplicates.length - 10}件</div>
                        )}
                      </div>
                    </div>
                  )}
                  {csvMappingModal.result.unknownRooms?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-red-800 mb-1">
                        ⚠️ 未登録の部屋（UI に表示されません）
                      </p>
                      <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 space-y-0.5">
                        {csvMappingModal.result.unknownRooms.map((u, i) => (
                          <div key={i}>「{u.room}」({u.count}件)</div>
                        ))}
                        <div className="mt-1 text-red-600">
                          → ルーム設定で「{csvMappingModal.result.unknownRooms.map(u => u.room).join('」「')}」を追加するか、CSV の部屋名を既存ID（OR1 等）に揃えてください。
                        </div>
                      </div>
                    </div>
                  )}
                  {csvMappingModal.result.unmatchedSkills?.length > 0 && (
                    <div className="mt-2 text-xs text-gray-700">
                      <strong>マスタ未登録の術式:</strong> {csvMappingModal.result.unmatchedSkills.slice(0, 8).join('、')}{csvMappingModal.result.unmatchedSkills.length > 8 ? ` …他${csvMappingModal.result.unmatchedSkills.length - 8}件` : ''}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-2">
                <button onClick={() => setCsvMappingModal({...csvMappingModal,show:false,result:null})} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm">閉じる</button>
                <button
                  onClick={importCsvData}
                  disabled={!canImport}
                  className={`px-5 py-2 rounded-md text-sm font-medium text-white ${canImport ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-300 cursor-not-allowed'}`}
                >
                  インポート実行
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* 自動人員配置モーダル */}
      {showAutoAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4" onClick={() => setShowAutoAssignModal(false)}>
          <div onClick={e=>e.stopPropagation()} className="w-full max-w-6xl max-h-[95vh] overflow-auto">
            <AutoStaffAssignmentModal
              isOpen={showAutoAssignModal}
              onClose={() => setShowAutoAssignModal(false)}
              date={selectedDate.toDate()}
              outpatientRequirement={currentOutpatientReq}
              onApplyAssignments={handleApplyAssignments}
            />
          </div>
        </div>
      )}

      {/* 情報収集モーダル */}
      {selectedSurgeryForInfo && (
        <SurgeryInfoModal
          surgery={selectedSurgeryForInfo}
          onClose={() => setSelectedSurgeryForInfo(null)}
          onUpdate={(updatedSurgery) => {
            setSurgeryData(prevData => {
              const updatedData = { ...prevData };
              Object.keys(updatedData).forEach(key => {
                if (updatedData[key].docId === updatedSurgery.id) {
                  updatedData[key] = { ...updatedData[key], ...updatedSurgery };
                }
              });
              return updatedData;
            });
          }}
        />
      )}
    </div>
  );
}

export default SurgeryRequest;